import React from 'react';
import { AlertTriangle, BookOpen, CheckCircle2, Maximize2, Minimize2, Minus, Moon, PanelRightClose, PanelRightOpen, Square, Sun, X } from 'lucide-react';
import { C } from './theme.js';
import { ResizeHandle } from './components/ResizeHandle.jsx';
import { Button, EnvToggle, IconButton, Logo, OrgPicker, UserPill } from './components/Primitives.jsx';
import { resetReviewOnImport } from './inspectors/transform/codeMode/caseWhenSlot.js';
import { CATALOG_DATASETS, SOURCE_COLUMNS, SOURCE_ROWS, STEP_TYPES, buildSourceEntries, findSheetNameCollision, findSourceNameOwner, pipelineOrder, sourceNameTakenMessage, summarizeStep } from './data.js';
import * as api from './api.js';
import { DataPreview, FileBrowserModal, FolderPickerModal, RunHistoryDrawer } from './preview/Preview.jsx';
import { DEFAULT_STEPS_PER_ROW, FlowCanvas, StepOutline } from './canvas/Canvas.jsx';
import { StepInspector } from './inspectors/StepInspector.jsx';
import { TweakColor, TweakSection, TweakToggle, TweaksPanel, useTweaks } from './tweaks/TweaksPanel.jsx';
import { LoginScreen, NewProjectModal, SsoSignInScreen, PipelinesScreen, ProjectsScreen, RunPipelineModal, SchedulePipelineModal } from './screens/Screens.jsx';
import { inferColumns } from './fileIO.js';

/* eslint-disable */
// ─── App shell: top bar + routing + state ────────────────────────────────

const APP_NAME = 'DataPipeline Studio';

// ── JSON import / export helpers ───────────────────────────────────────
// Filenames are normalised to a slug so the export feels predictable.
function slugify(s) {
  return String(s || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
function downloadJSON(filename, data) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
// Programmatically open the OS file picker for a JSON file. The callback
// receives (data, filename, error?) — `data` is null when the file failed
// to parse, so the caller can surface the error in their own way.
function pickJSONFile(onLoad) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.onchange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try { onLoad(JSON.parse(ev.target.result), file.name); }
      catch (err) { onLoad(null, file.name, err); }
    };
    reader.readAsText(file);
  };
  input.click();
}
// Fresh ID factories — used to avoid collisions when an imported project /
// pipeline / step lands next to existing ones.
const rid = (prefix) => prefix + Math.random().toString(36).slice(2, 7);

// ── Toast notification ─────────────────────────────────────────────────
// Lightweight, top-right, auto-dismisses after 2.5s.
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const tone = toast.kind === 'error'
    ? { bg: 'var(--error-bg)', bd: C.error, fg: C.error, icon: X }
    : toast.kind === 'warn'
      ? { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn, icon: AlertTriangle }
      : { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok, icon: CheckCircle2 };
  const ToneIcon = tone.icon;
  return (
    <div style={{
      position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)', zIndex: 2200, // above modals and the floating inspector
      display: 'flex', alignItems: 'center', gap: 10,
      background: tone.bg, border: `1px solid ${tone.bd}`,
      borderRadius: 8, padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,.45)',
      maxWidth: 380,
    }}>
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: tone.bd, color: 'var(--bg-app)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}><ToneIcon size={13} strokeWidth={2.5} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          color: C.fg1, fontSize: '.84rem', fontWeight: 600,
        }}>{toast.title}</div>
        {toast.detail && (
          <div style={{
            color: C.fg4, fontSize: '.74rem', marginTop: 2,
            fontFamily: toast.detailMono ? "'JetBrains Mono', monospace" : 'inherit',
          }}>{toast.detail}</div>
        )}
      </div>
      <button onClick={onClose} style={{
        background: 'transparent', border: 'none', color: C.fg4,
        cursor: 'pointer', display: 'inline-flex', fontFamily: 'inherit',
        padding: '0 2px',
      }}><X size={14} /></button>
    </div>
  );
}

// ── Help button (?) shown in TopBar on every screen ────────────────────
// Opens Documentation.html in a new tab so the user can keep their work in view.
function HelpButton() {
  const [hover, setHover] = React.useState(false);
  return (
    <a
      href="/Documentation.html"
      target="_blank"
      rel="noopener"
      title="Documentation (?)"
      aria-label="Open documentation"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '50%',
        background: hover ? 'var(--info-bg)' : 'transparent',
        color: hover ? C.accentBright : C.fg4,
        border: `1px solid ${hover ? C.accentMuted : C.borderStrong}`,
        fontWeight: 700, fontSize: '.86rem',
        textDecoration: 'none',
        transition: 'all .12s',
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >?</a>
  );
}

// ── Theme toggle button (used in TopBar) ───────────────────────────────
// Click to flip dark ⇄ light. We show the glyph of the *destination* theme
// (sun in dark mode, moon in light mode) so the icon describes the action,
// not the current state — a common pattern.
function ThemeButton({ theme, onToggle }) {
  const [hover, setHover] = React.useState(false);
  const next = theme === 'light' ? 'dark' : 'light';
  const Glyph = theme === 'light' ? Moon : Sun;
  return (
    <button
      onClick={onToggle}
      title={`Switch to ${next} theme`}
      aria-label={`Switch to ${next} theme`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '50%',
        background: hover ? 'var(--info-bg)' : 'transparent',
        color: hover ? C.accentBright : C.fg4,
        border: `1px solid ${hover ? C.accentMuted : C.borderStrong}`,
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
    ><Glyph size={14} /></button>
  );
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#3b82f6",
  "canvasDots": true,
  "compactNodes": false,
  "showColumnTypes": true,
  "showStepBadges": true
}/*EDITMODE-END*/;

// ── Options dropdown menu (used in TopBar) ──────────────────────────────
function OptionsMenu({ onReplaceInput, onOpenRuns, onExportPipeline, pipeline, panels, togglePanel }) {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', background: 'transparent',
    border: 'none', color: C.fg1, fontSize: '.82rem',
    fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
    textAlign: 'left', width: '100%',
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => setOpen(o => !o)}
      >
        Options <span style={{ fontSize: '.65rem', marginLeft: 4 }}>▼</span>
      </Button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 150,
          background: C.surface,
          border: `1px solid ${C.borderStrong}`,
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.45)',
          padding: '4px 0',
          minWidth: 190,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <button
            onClick={() => { setOpen(false); onReplaceInput?.(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', background: 'transparent',
              border: 'none', color: C.fg1, fontSize: '.82rem',
              fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
              textAlign: 'left', width: '100%',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Replace input
          </button>
          <button
            onClick={() => { setOpen(false); onOpenRuns?.(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', background: 'transparent',
              border: 'none', color: C.fg1, fontSize: '.82rem',
              fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
              textAlign: 'left', width: '100%',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Run history
          </button>
          <button
            onClick={() => { setOpen(false); onExportPipeline?.(pipeline); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', background: 'transparent',
              border: 'none', color: C.fg1, fontSize: '.82rem',
              fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
              textAlign: 'left', width: '100%',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Export
          </button>

          <div style={{ height: 1, background: C.border, margin: '4px 0' }} />
          <div style={{
            padding: '6px 12px 2px', color: C.fg5, fontSize: '.66rem',
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
          }}>View</div>
          <button
            onClick={() => { setOpen(false); togglePanel?.('left'); }}
            style={itemStyle}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span>◧</span> {panels?.left ? 'Hide add steps' : 'Show add steps'}
          </button>
          <button
            onClick={() => { setOpen(false); togglePanel?.('preview'); }}
            style={itemStyle}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span>⬓</span> {panels?.preview ? 'Hide data preview' : 'Show data preview'}
          </button>

          <div style={{ height: 1, background: C.border, margin: '4px 0' }} />
          <a
            href="/Documentation.html"
            target="_blank"
            rel="noopener"
            onClick={() => setOpen(false)}
            style={{ ...itemStyle, textDecoration: 'none', boxSizing: 'border-box' }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.input}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{ display: 'inline-flex' }}><BookOpen size={14} /></span> Documentation
          </a>
        </div>
      )}
    </div>
  );
}

// ── Top bar ────────────────────────────────────────────────────────────
function TopBar({ project, pipeline, view, onHome, onBackToPipelines, onRun, onOpenRuns, onReplaceInput, onExportPipeline, onSetEnv, onSave, user, onLogout, onSwitchOrg, orgs, dirty, panels, togglePanel, theme, onToggleTheme }) {
  return (
    <div style={{
      height: 52, flexShrink: 0,
      display: 'flex', alignItems: 'center',
      padding: '0 16px',
      background: C.bar,
      borderBottom: `1px solid ${C.borderStrong}`,
      gap: 14,
    }}>
      <button onClick={onHome} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: 'inherit', padding: 0,
      }}>
        <Logo size={22} />
        <span style={{ color: C.fg1, fontWeight: 700, fontSize: '.94rem', letterSpacing: '-.005em' }}>
          Data<span style={{ color: C.fg5, fontWeight: 400 }}>Studio</span>
        </span>
      </button>

      {project && (
        <>
          <span style={{ color: C.fg6, fontSize: '.75rem' }}>/</span>
          {pipeline ? (
            <button onClick={onBackToPipelines} style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.fg5, fontSize: '.75rem', fontFamily: 'inherit', padding: 0,
            }} title="Back to pipelines">{project.name}</button>
          ) : (
            <span style={{ color: C.fg4, fontSize: '.75rem' }}>{project.name}</span>
          )}
          {pipeline && (
            <>
              <span style={{ color: C.fg6, fontSize: '.75rem' }}>/</span>
              <span style={{ color: C.fg2, fontSize: '.75rem', fontWeight: 600 }}>{pipeline.name}</span>
            </>
          )}
          {dirty && (
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: C.warn, marginLeft: 4,
            }} title="Unsaved changes" />
          )}
        </>
      )}


      <div style={{ flex: 1 }} />

      {view === 'pipeline' && (
        <>
          <Button kind="secondary" size="sm" onClick={() => onSave?.()}>Save</Button>
          <Button kind="primary" size="md" icon="▶" onClick={onRun}>Run pipeline</Button>
          <EnvToggle
            value={pipeline?.env || 'test'}
            onChange={(env) => onSetEnv?.(env)}
          />
          <OptionsMenu
            onReplaceInput={onReplaceInput}
            onOpenRuns={onOpenRuns}
            onExportPipeline={onExportPipeline}
            pipeline={pipeline}
            panels={panels}
            togglePanel={togglePanel}
          />
          <ThemeButton theme={theme} onToggle={onToggleTheme} />
          <UserPill user={user} onLogout={onLogout} />
        </>
      )}

      {view !== 'pipeline' && (
        <>
          <a
            href="/Documentation.html"
            target="_blank"
            rel="noopener"
            style={{ textDecoration: 'none' }}
          >
            <Button kind="ghost" size="sm" icon={<BookOpen size={14} />}>Docs</Button>
          </a>
          {view === 'projects' && (
            <OrgPicker
              orgs={orgs || []}
              current={user?.organization}
              onSwitch={onSwitchOrg}
            />
          )}
          <ThemeButton theme={theme} onToggle={onToggleTheme} />
          <HelpButton />
          <UserPill user={user} onLogout={onLogout} />
        </>
      )}
    </div>
  );
}

// ── Configuration panel docking preference, remembered per pipeline ─────
// Local-only (like the floating panel's own position/size — this never
// touches the server): localStorage, same 'dpstudio.*' key convention and
// try/catch-on-quota as the theme preference below. Absent (nothing ever
// saved for this pipeline — a brand-new pipeline, or one from before this
// preference existed) defaults to docked, per the explicit ask that a new
// pipeline opens with the configuration panel docked.
const INSPECTOR_DOCK_PREFIX = 'dpstudio.inspectorDocked.';
function readInspectorDocked(pipelineId) {
  try {
    const raw = window.localStorage.getItem(INSPECTOR_DOCK_PREFIX + pipelineId);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}
function writeInspectorDocked(pipelineId, docked) {
  try { window.localStorage.setItem(INSPECTOR_DOCK_PREFIX + pipelineId, docked ? '1' : '0'); } catch { /* quota */ }
}

// ── Project (flow editor) screen ────────────────────────────────────────
function PipelineEditor({ project, projects, pipeline: initialPipeline, onUpdatePipeline, onRun, onOpenRuns, density, accentOverride, dirty, setDirty, panels, togglePanel, uploadedData, onUploadData, onToast, onRefreshSchema, lastRunId }) {
  const [pipeline, setPipeline] = React.useState(initialPipeline);
  // Re-seed local state if the user navigates to a different pipeline
  // (the editor stays mounted across pipeline switches).
  React.useEffect(() => {
    // Switching pipelines starts a fresh undo history; the re-seed itself
    // must not be recorded as an edit (skipped only if it really changes state).
    pastRef.current = []; futureRef.current = []; lastEditRef.current = null;
    if (pipeline !== initialPipeline) skipRecordRef.current = true;
    setPipeline(initialPipeline);
    setHistVersion(v => v + 1);
    setInspectorDocked(readInspectorDocked(initialPipeline.id));
  }, [initialPipeline?.id]);

  // ── Undo / redo ─────────────────────────────────────────────────────
  // History is a list of past/future snapshots of the user-editable parts of
  // the pipeline (steps, output settings, env). It lives only in the editor,
  // never touches autosave: undoing is just another edit that autosave then
  // persists. A change is detected by watching `pipeline` itself, so none of
  // the many setPipeline call sites needed changing. Input-file/sheet
  // changes aren't part of a snapshot, so uploads are neither recorded nor
  // undone. Keystrokes in the same step within 600ms merge into one entry.
  const HISTORY_LIMIT = 50;
  const snapOf = (pl) => ({ steps: pl.steps, output: pl.output, env: pl.env });
  const pastRef = React.useRef([]);
  const futureRef = React.useRef([]);
  const prevSnapRef = React.useRef(snapOf(initialPipeline));
  const skipRecordRef = React.useRef(false);
  const lastEditRef = React.useRef(null); // { id, time } of the last merged config edit
  const [histVersion, setHistVersion] = React.useState(0);

  // The single step whose config changed (same ids/order/positions), else null.
  const singleEditedStep = (a, b) => {
    if (!a || !b || a.length !== b.length) return null;
    let id = null;
    for (let i = 0; i < a.length; i++) {
      if (a[i] === b[i]) continue;
      const x = a[i], y = b[i];
      if (x.id !== y.id || x.order !== y.order || x.x !== y.x || x.y !== y.y || x.type !== y.type) return null;
      if (id) return null;
      id = x.id;
    }
    return id;
  };

  React.useEffect(() => {
    const snap = snapOf(pipeline);
    const prev = prevSnapRef.current;
    prevSnapRef.current = snap;
    if (skipRecordRef.current) { skipRecordRef.current = false; return; }
    if (!prev || (prev.steps === snap.steps && prev.output === snap.output && prev.env === snap.env)) return;
    const editedId = prev.output === snap.output && prev.env === snap.env
      ? singleEditedStep(prev.steps, snap.steps) : null;
    const now = Date.now();
    const last = lastEditRef.current;
    const merge = editedId && last && last.id === editedId && now - last.time < 600;
    lastEditRef.current = editedId ? { id: editedId, time: now } : null;
    futureRef.current = [];
    if (!merge) {
      pastRef.current.push(prev);
      if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
    }
    setHistVersion(v => v + 1);
  }, [pipeline]);

  const applySnapshot = (target) => {
    skipRecordRef.current = true;
    lastEditRef.current = null;
    setDirty(true);
    setPipeline(p => ({ ...p, ...target }));
    if (!target.steps.some(st => st.id === selectedIdRef.current)) setSelectedId(null);
    setHistVersion(v => v + 1);
  };
  const undo = () => {
    const target = pastRef.current.pop();
    if (!target) return;
    futureRef.current.push(prevSnapRef.current);
    applySnapshot(target);
  };
  const redo = () => {
    const target = futureRef.current.pop();
    if (!target) return;
    pastRef.current.push(prevSnapRef.current);
    applySnapshot(target);
  };
  const undoRef = React.useRef(undo); undoRef.current = undo;
  const redoRef = React.useRef(redo); redoRef.current = redo;
  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  void histVersion;
  React.useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redoRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Push local edits back up to the project catalog so they survive
  // navigating away from this pipeline.
  React.useEffect(() => {
    if (pipeline && pipeline.id === initialPipeline?.id) onUpdatePipeline?.(pipeline);
     
  }, [pipeline]);
  const [selectedId, setSelectedId] = React.useState(() => pipeline.steps.find(s => s.type === 'source')?.id || pipeline.steps[0]?.id);
  // Loading a different pipeline (the editor stays mounted across
  // switches, same as the undo-history/dock-preference re-seed above)
  // always lands on its Source block, not whatever was selected before.
  React.useEffect(() => {
    setSelectedId(initialPipeline.steps.find(s => s.type === 'source')?.id || initialPipeline.steps[0]?.id || null);
  }, [initialPipeline?.id]);
  const selectedIdRef = React.useRef(selectedId);
  selectedIdRef.current = selectedId;
  // The inspector is a floating window, not a docked column. It opens
  // automatically whenever the user selects a *different* step, even if
  // they'd previously dismissed it with the panel's own "×" close button —
  // but once open, it stays open (deselecting, e.g. by clicking empty
  // canvas, must not silently close it too; only "×" should). `prevIdRef`
  // is what makes that distinction: without it, this effect would re-fire
  // and re-open the panel the instant "×" flips `panels.right` to false,
  // since that's also a dependency here.
  const prevSelectedIdRef = React.useRef(selectedId);
  React.useEffect(() => {
    const changed = selectedId !== prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedId;
    if (changed && selectedId && !panels.right) togglePanel('right');
  }, [selectedId, panels.right, togglePanel]);
  // Most recently inserted step — lets the canvas play a "spawn" animation
  // (scale-in + halo) so the user's eye lands on the new node.
  const [lastAddedStepId, setLastAddedStepId] = React.useState(null);
  const lastAddedTimerRef = React.useRef(null);
  const [previewHeight, setPreviewHeight] = React.useState(240);
  const [leftWidth, setLeftWidth] = React.useState(260);
  const [resizing, setResizing] = React.useState(false);
  const resizeStart = React.useRef(0);
  // Configuration panel: floating (draggable anywhere) or docked to the
  // right of the canvas only — Data Preview, below, always stays full
  // width either way. Which one is remembered per pipeline (localStorage,
  // see readInspectorDocked/writeInspectorDocked above); the re-seed effect
  // above re-reads it on every pipeline switch. Width itself is session-
  // only, same convention as the floating panel's own position/size.
  const [inspectorDocked, setInspectorDocked] = React.useState(() => readInspectorDocked(initialPipeline.id));
  const [dockedWidth, setDockedWidth] = React.useState(380);
  const dockInspector = (docked) => {
    setInspectorDocked(docked);
    writeInspectorDocked(pipeline.id, docked);
  };

  // Per-step test results from "Test this step". status = 'running' | 'done' | 'error'.
  const [testResults, setTestResults] = React.useState(() => new Map());
  // Bumped after a successful test to briefly flash the data preview.
  const [flashTick, setFlashTick] = React.useState(0);
  // File browser modal state
  const [fileBrowserOpen, setFileBrowserOpen] = React.useState(false);
  const [folderPickerOpen, setFolderPickerOpen] = React.useState(false);
  // Uploaded join/lookup files (Map<filename, { rows, columns, fileName }>),
  // persisted as kind="file" Dataset rows (server/app/routers/datasets.py).
  // They share one Source namespace with sheets and step Output Names.
  const [secondaryDatasets, setSecondaryDatasets] = React.useState(() => new Map());
  // Sheets kept from an upload (Map<sheetName, { rows, columns }>), persisted
  // as kind="sheet" Dataset rows so they survive a reload.
  const [sheets, setSheets] = React.useState(() => new Map());
  // Which sheet or saved step output Data Preview's View picker is showing
  // (a previewSources key) — null = the pipeline's own view (the default).
  const [activeSheet, setActiveSheet] = React.useState(null);
  // Data Preview scope: 'step' shows the selected step's Before/After,
  // 'final' always shows the pipeline's final output whatever is selected.
  const [previewScope, setPreviewScope] = React.useState('step');
  // Bumped after the stored input file is cleared server-side, to force one
  // more /preview once the delete has actually landed (see onPickInputFile).
  // Bumped once a server-side input/source write has landed (stored input
  // file cleared, sheets or files saved) to force one more /preview: the
  // server builds the Source registry from the DB, so a preview that runs
  // before the write lands would miss it.
  const [inputEpoch, setInputEpoch] = React.useState(0);
  // Hydrate sheets from the server whenever the pipeline changes (covers
  // both "just opened this pipeline" and switching to a different one).
  // Hydrate sheets and uploaded files from the server whenever the pipeline
  // changes (covers both "just opened this pipeline" and switching pipelines).
  React.useEffect(() => {
    if (!pipeline?.id) return undefined;
    let cancelled = false;
    setActiveSheet(null);
    // Clear synchronously, before the fetch resolves — this component
    // stays mounted across pipeline switches (no `key` on PipelineEditor),
    // so without this, whatever sheet/file was added via "Add file…" in
    // the PREVIOUS pipeline stays visible and pickable in every Source
    // dropdown here until the GET below lands. That's a real cross-pipeline
    // leak in the UI, even though each pipeline's Dataset rows are already
    // correctly isolated server-side by pipelineId.
    setSheets(new Map());
    setSecondaryDatasets(new Map());
    api.listSheets(pipeline.id).then((list) => {
      if (cancelled) return;
      const m = new Map();
      for (const s of list) m.set(s.name, { rows: s.rows, columns: s.columns, id: s.id, order: s.order });
      setSheets(m);
      const sheetMap = new Map();
      const fileMap = new Map();
      for (const s of list) {
        if (s.kind === 'file') fileMap.set(s.name, { rows: s.rows, columns: s.columns, fileName: s.name, id: s.id, order: s.order });
        else sheetMap.set(s.name, { rows: s.rows, columns: s.columns, id: s.id, order: s.order });
      }
      setSheets(sheetMap);
      setSecondaryDatasets(fileMap);
    }).catch(() => {
      // Non-fatal — sheets just won't be available as sources this session.
      // Non-fatal — sheets/files just won't be listed as sources this session.
    });
    return () => { cancelled = true; };
  }, [pipeline?.id]);
  // Join file browser modal state
  const [joinBrowserOpen, setJoinBrowserOpen] = React.useState(false);
  const [joinBrowserStepId, setJoinBrowserStepId] = React.useState(null);
  // Optional per-open callback: which field the picked file should be written
  // to (e.g. Lookup's Source / Driving Source). Without one, the pick sets
  // Join/Union's `right`.
  const joinBrowserOnPickedRef = React.useRef(null);
  // Section fullscreen: null | 'canvas' | 'preview'.
  // When set, the other workspace columns + preview/canvas are hidden so the
  // chosen section takes the entire editor area.
  const [fullscreen, setFullscreen] = React.useState(null);
  const toggleFullscreen = React.useCallback((section) => {
    setFullscreen(curr => curr === section ? null : section);
  }, []);
  // In canvas fullscreen the inspector is hidden; double-clicking a step
  // opens it as a floating window over the canvas. Closed again by its "×",
  // Esc, deselecting, or leaving fullscreen.
  const [fsInspectorOpen, setFsInspectorOpen] = React.useState(false);
  React.useEffect(() => { if (fullscreen !== 'canvas') setFsInspectorOpen(false); }, [fullscreen]);
  // Esc closes that window first, then exits fullscreen.
  React.useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (fsInspectorOpen) setFsInspectorOpen(false);
      else setFullscreen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen, fsInspectorOpen]);

  // Execute the pipeline up to each step. Debounced server call: the editor
  // produces many state changes per second and we don't want a /preview round
  // trip on every keystroke. While the new map is in flight we keep showing
  // the stale one so the canvas doesn't flicker.
  const [runMap, setRunMap] = React.useState(() => new Map());
  // Column lineage from the same /preview call (engine/column_usage.py): which
  // steps read/write each column family. `focusColumn` is the column the user
  // clicked in Data Preview; the canvas highlights every block that touches it.
  const [columnUsage, setColumnUsage] = React.useState(null);
  const [focusColumn, setFocusColumn] = React.useState(null); // { fid, name } | null
  // True from the moment something that would change the preview happens
  // (still waiting out the debounce, or the /preview request itself is in
  // flight) until runMap is updated — Data Preview's Download button stays
  // disabled the whole time, so a downloaded file always matches what's on
  // screen instead of racing a table that's about to change under it.
  const [previewPending, setPreviewPending] = React.useState(false);
  // Sources (sheets, uploaded files, step outputs) are resolved server-side
  // from the DB, so only the steps and input rows are sent. `sheets`/
  // `secondaryDatasets` stay as effect dependencies so a fresh
  // upload/save re-previews immediately, without waiting on `inputEpoch`.
  React.useEffect(() => {
    if (!pipeline?.id) return undefined;
    setPreviewPending(true);
    const tid = setTimeout(async () => {
      try {
        const res = await api.preview(pipeline.id, {
          inputRows: pipeline.inputFile?.storagePath ? [] : uploadedData.rows,
          steps: pipeline.steps,
        });
        const m = new Map();
        for (const [sid, r] of Object.entries(res.runMap || {})) m.set(sid, r);
        setRunMap(m);
        setColumnUsage(res.columnUsage || null);
      } catch (e) {
        // Preview failures are otherwise non-fatal (leave the stale map in
        // place) — but a pre-run pipeline validation failure (duplicate
        // output_name, a missing column reference, an unrecognized
        // operator/function — see pipeline_validate.py) is new, common
        // (can trip on every keystroke while a pipeline is mid-edit), and
        // silent otherwise, so it gets a toast naming the problem instead
        // of the preview panel just going stale with no explanation.
        if (e?.violations?.length) {
          onToast?.({ kind: 'error', title: 'Pipeline has a validation problem', detail: e.message });
        }
      } finally {
        setPreviewPending(false);
      }
    }, 150);
    return () => clearTimeout(tid);
  // lastRunId: a finished run saves HTTP Request results the preview shows,
  // so preview again once it lands.
  }, [pipeline, uploadedData.rows, secondaryDatasets, sheets, inputEpoch, onToast, lastRunId]);

  // Data Preview's "Download full result" button — a real browser file
  // download from a POST response, so this fetches a blob and saves it via
  // an object URL rather than reusing onRun's GET-anchor pattern (which
  // only works because that endpoint is a plain GET the browser can
  // navigate to directly).
  const onDownloadStep = React.useCallback(async (step, format) => {
    if (!pipeline?.id || !step) return;
    try {
      const { blob, filename } = await api.downloadStepResult(pipeline.id, step.id, {
        inputRows: pipeline.inputFile?.storagePath ? [] : uploadedData.rows,
        steps: pipeline.steps,
        format,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      onToast?.({ kind: 'error', title: 'Download failed', detail: e.message });
    }
  }, [pipeline, uploadedData.rows, onToast]);

  const selectedStep = pipeline.steps.find(s => s.id === selectedId);
  const ordered = pipelineOrder(pipeline.steps);
  const selIdx = ordered.findIndex(s => s.id === selectedId);

  // Before / after rows for the data preview
  const beforeRows = React.useMemo(() => {
    if (selIdx <= 0) return uploadedData.rows;
    return runMap.get(ordered[selIdx - 1].id)?.rows || uploadedData.rows;
  }, [selectedId, runMap, uploadedData.rows]);
  const afterRows = React.useMemo(() => {
    if (!selectedStep) return uploadedData.rows;
    return runMap.get(selectedStep.id)?.rows || uploadedData.rows;
  }, [selectedId, runMap, uploadedData.rows]);

  // "Final output" preview: the last step in pipeline order that has a
  // preview result (normally the Output step, which passes rows through).
  const finalStep = React.useMemo(() => {
    for (let i = ordered.length - 1; i >= 0; i--) {
      if (runMap.has(ordered[i].id)) return ordered[i];
    }
    return ordered[ordered.length - 1] || null;
  }, [pipeline.steps, runMap]);
  const showFinal = previewScope === 'final';
  const previewStep = showFinal ? finalStep : selectedStep;
  const previewAfterRows = React.useMemo(() => {
    if (!showFinal) return afterRows;
    return (finalStep && runMap.get(finalStep.id)?.rows) || uploadedData.rows;
  }, [showFinal, afterRows, finalStep, runMap, uploadedData.rows]);
  // No Before view for the final output, so both sides are the same rows.
  const previewBeforeRows = showFinal ? previewAfterRows : beforeRows;

  // What Data Preview's "View" picker can switch to besides the pipeline:
  // every kept sheet, then every step that saves its result under an Output
  // Name (rows from the last /preview, so at most PREVIEW_ROW_CAP of them).
  // `key` is kind-prefixed so a sheet and a step output can't collide;
  // `name` is the Source name, which keys columnUsage.tables.
  const previewSources = React.useMemo(() => {
    const list = Array.from(sheets, ([name, v]) => ({
      key: `sheet:${name}`, kind: 'sheet', name, rows: v.rows || [], columns: v.columns || [],
    }));
    for (const st of ordered) {
      const name = (st.config?.output_name || '').trim();
      if (!name || st.disabled) continue;
      const info = runMap.get(st.id);
      const rows = info?.rows || [];
      list.push({
        key: `step:${st.id}`, kind: 'step', name, stepLabel: st.label || STEP_TYPES[st.type]?.label || st.type,
        rows, columns: rows.length ? inferColumns(rows) : [],
        totalRows: info?.outputCount ?? rows.length, error: info?.error || null,
      });
    }
    return list;
  }, [sheets, pipeline.steps, runMap]);
  const activeSource = activeSheet != null ? previewSources.find(s => s.key === activeSheet) || null : null;

  // Column-family maps for what Data Preview is showing (before / after of the
  // selected step, or the active sheet tab), so a clicked header resolves to a
  // column family id.
  const columnFids = React.useMemo(() => {
    if (!columnUsage) return null;
    const steps = columnUsage.steps || {};
    const initial = columnUsage.initial || {};
    return {
      before: selIdx <= 0 ? initial : (steps[ordered[selIdx - 1].id]?.after || initial),
      after: previewStep ? (steps[previewStep.id]?.after || initial) : initial,
      sheet: activeSource ? (columnUsage.tables || {})[activeSource.name] : null,
    };
  }, [columnUsage, selectedId, selIdx, activeSource, previewStep]);

  // Columns the selected step writes (creates, modifies or removes), by name,
  // for each side of the Current Step preview — Data Preview tints them.
  // Not used in Final Output mode, which isn't about one step.
  const modifiedColumns = React.useMemo(() => {
    if (showFinal || !selectedStep || !columnFids) return null;
    const touches = columnUsage?.steps?.[selectedStep.id]?.touches || {};
    const written = (fidMap) => new Set(Object.entries(fidMap || {})
      .filter(([, fid]) => (touches[fid] || '').includes('w'))
      .map(([name]) => name));
    return { before: written(columnFids.before), after: written(columnFids.after) };
  }, [showFinal, selectedStep, columnFids, columnUsage]);

  const onPickColumn = React.useCallback((fid, name) => {
    setFocusColumn(cur => (cur && cur.fid === fid ? null : { fid, name }));
  }, []);

  // The blocks that touch the clicked column: id → 'r' | 'w' | 'rw', plus the
  // ids in pipeline order (the canvas scrolls to the first and steps through).
  const columnFocus = React.useMemo(() => {
    if (!focusColumn) return null;
    const stepsUsage = columnUsage?.steps || {};
    const highlights = new Map();
    const order = [];
    for (const st of ordered) {
      const role = stepsUsage[st.id]?.touches?.[focusColumn.fid];
      if (role && !st.disabled) { highlights.set(st.id, role); order.push(st.id); }
    }
    return { key: focusColumn.fid, label: focusColumn.name, highlights, order };
  }, [focusColumn, columnUsage, pipeline.steps]);

  React.useEffect(() => {
    if (!focusColumn) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      setFocusColumn(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusColumn]);

  React.useEffect(() => { setFocusColumn(null); setColumnUsage(null); }, [pipeline?.id]);

  // Source `uploadedData.columns` is the input schema only. Scaling (and
  // compute/geocode/…) ADD keys — merge those into the table header or the
  // new columns are computed server-side and never shown.
  const previewColumns = React.useMemo(
    () => columnsFromRows(previewAfterRows, uploadedData.columns),
    [previewAfterRows, uploadedData.columns],
  );
  // Inspector chips should reflect columns arriving AT this step, with types
  // inferred from values so currency strings like mrr="$480" count as numeric.
  const inspectorColumns = React.useMemo(
    () => (beforeRows?.length ? inferColumns(beforeRows) : uploadedData.columns),
    [beforeRows, uploadedData.columns],
  );
  // Original source-file schema, independent of any step — this is what the
  // scaling/encoding column pickers use (via OriginalColumnsContext) so a
  // column an earlier scaling/encoding step added is never itself offered.
  // Same currency-aware inference as inspectorColumns, run over the
  // untouched upload rows instead of whatever reaches the selected step.
  const originalColumns = React.useMemo(
    () => (uploadedData.rows?.length ? inferColumns(uploadedData.rows) : uploadedData.columns),
    [uploadedData.rows, uploadedData.columns],
  );

  const onReplaceInput = React.useCallback(() => setFileBrowserOpen(true), []);
  const onPickInputFile = React.useCallback((file) => {
    setDirty(true);
    // If this is an uploaded file with parsed data, update uploadedData.
    if (file.uploaded && file._parsedRows) {
      const rows = file._parsedRows.map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i }));
      onUploadData({ rows, columns: file._parsedColumns, fileName: file.name });
      // A new primary input replaces the old one, so any sheets kept from a
      // *previous* multi-sheet upload no longer apply — clear them locally
      // right away (the server clears its copies as part of uploadInputFile
      // below) so stale tabs don't linger until the next listSheets refetch.
      setSheets(new Map());
      if (file._rawFile && pipeline?.id) {
        const pipelineId = pipeline.id;
        (async () => {
          try {
            // Must land BEFORE the sheet upserts below: upload_input clears
            // every kind="sheet" row for this pipeline as part of storing the
            // new file, so upserting sheets first would have them wiped out
            // the moment the upload's own clear-and-parse finishes.
            const res = await api.uploadInputFile(pipelineId, file._rawFile, file.sheetName, file.blankBuildTarget, file.buildTargetName);
            if (res.inputFile) {
              setPipeline(prev => ({ ...prev, inputFile: { ...prev.inputFile, ...res.inputFile } }));
            }
          } catch (_e) {
            // Upload failure is non-fatal — user can still work with client-side
            // data, but the sheets below would have nothing to attach to.
            return;
          }
          // Every kept sheet of a multi-sheet workbook — including the first
          // one, which also serves as the primary input — becomes a
          // persisted named source (server/app/routers/datasets.py) and
          // therefore its own Data Preview tab. `order` is the sheet's
          // position in the workbook, so tabs render in workbook order
          // regardless of upsert timing.
          for (let i = 0; i < (file.sheets || []).length; i++) {
            const sheet = file.sheets[i];
            const sheetRows = sheet.rows.map((r, idx) => ({ ...r, id: r.id || `row_${idx}`, __rowIndex: idx }));
            setSheets(prev => {
              const next = new Map(prev);
              next.set(sheet.name, { rows: sheetRows, columns: sheet.columns });
              return next;
            });
            try {
              await api.upsertSheet(pipelineId, sheet.name, { kind: 'sheet', rows: sheetRows, columns: sheet.columns, order: i });
            } catch (_e) {
              // Non-fatal — the sheet still works this session from local state,
              // it just won't survive a reload until the next successful save.
            }
          }
          setInputEpoch(n => n + 1);
        })();
      }
    } else {
      // This pick carries no uploaded bytes: a "Browse" catalog entry, a
      // folder watcher, or a chained pipeline. Two things used to go wrong —
      // uploadedData kept the PREVIOUS file's rows (so Data Preview and the
      // detected schema still showed the old file while the name badge showed
      // the new one), and the previous file stayed on disk server-side, where
      // _resolve_input_rows falls back to it and re-serves it into runMap.
      // Replace both: swap in whatever real rows this pick has, and drop the
      // stored file so nothing can resurrect the old one.
      const demo = CATALOG_DATASETS[file.name];
      if (demo) {
        const rows = demo.rows.map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i }));
        onUploadData({ rows, columns: demo.columns, fileName: file.name });
      } else {
        // Nothing ships this file's contents, so show no rows rather than a
        // different file's — Data Preview falls back to its existing
        // "N rows on file — re-upload to preview" placeholder.
        onUploadData({ rows: [], columns: [], fileName: null });
      }
      setSheets(new Map());
      if (pipeline?.id) {
        api.clearInputFile(pipeline.id)
          // Re-preview once the stored file is actually gone, otherwise a
          // preview racing the delete still reads the old file off disk.
          .then(() => setInputEpoch(n => n + 1))
          .catch((_e) => {
            // Non-fatal — the local swap above already happened.
          });
      }
    }
    setPipeline(p => {
      const isDyn = !!file.dynamic;
      const isChain = !!file.chained;
      const sizeStr = (file.size || '').toString().trim();
      const next = {
        ...p,
        inputFile: isDyn
          ? {
            name: file.name,
            format: file.format,
            size: sizeStr || '—',
            rows: file.rows || 0,
            dynamic: true,
            watchPath: file.watchPath,
            watchPattern: file.watchPattern,
            nextUp: file.nextUp || null,
            matchCount: file.matchCount || 0,
          }
          : isChain
            ? {
              name: file.name,
              format: file.format,
              size: sizeStr || '—',
              rows: file.rows || 0,
              chained: true,
              sourcePipelineId: file.sourcePipelineId,
              sourcePipelineName: file.sourcePipelineName,
              sourceProjectId: file.sourceProjectId,
              sourceProjectName: file.sourceProjectName,
              sourceOutputFolder: file.sourceOutputFolder,
              sourceOutputPattern: file.sourceOutputPattern,
              sourceLastRun: file.sourceLastRun,
              sourceStatus: file.sourceStatus,
            }
            : {
              name: file.name,
              format: file.format,
              size: sizeStr,
              rows: file.rows,
            },
        // Relabel the source step to match (only the prefix is auto-managed).
        steps: p.steps.map(s =>
          s.type === 'source'
            ? {
              ...s,
              label: isDyn
                ? `Source · Watch ${file.watchPath}${file.watchPattern || '*'}`
                : isChain
                  ? `Source · ⇠ ${file.sourcePipelineName}`
                  : `Source · ${file.name}`,
            }
            : s
        ),
      };
      return next;
    });
    // Tests done against the old file are now stale.
    setTestResults(new Map());
    setFileBrowserOpen(false);
  }, [setDirty, onUploadData, pipeline?.id]);

  // Output config edits (folder, filename pattern, format, write mode, seq range).
  // Also mirrors format/filename onto the Output step's own `label`/`config` —
  // those are what the canvas card's title/summary (summarizeStep, data.js)
  // and the step header elsewhere actually read. Both were set once when the
  // step was created and never touched again, so switching the real output
  // format here (e.g. CSV → XLSX) left the canvas showing "CSV" forever.
  // Nothing else reads an output step's own config.format/filename — the
  // server only ever looks at pipeline.outputJson for a real run — so this
  // mirror is purely cosmetic and safe.
  const onChangeOutput = React.useCallback((nextOutput) => {
    setDirty(true);
    setPipeline(p => {
      const output = { ...p.output, ...nextOutput };
      // "Append to input file" mode always writes XLSX regardless of the
      // `format` field — see OutputInspector's own note on that mode.
      const effectiveFormat = output.saveAs === 'append_to_input' ? 'xlsx' : (output.format || 'csv');
      const steps = p.steps.map(s => (s.type === 'output'
        ? { ...s, label: `Output · ${p.name}.${effectiveFormat}`, config: { ...s.config, format: effectiveFormat, filename: output.filename } }
        : s));
      return { ...p, output, steps };
    });
  }, [setDirty]);
  const onBrowseFolder = React.useCallback(() => setFolderPickerOpen(true), []);
  const onPickFolder = React.useCallback((path) => {
    onChangeOutput({ folder: path });
    setFolderPickerOpen(false);
  }, [onChangeOutput]);

  // Join file browser callbacks
  const onBrowseJoinFile = React.useCallback((stepId, onPicked) => {
    joinBrowserOnPickedRef.current = onPicked || null;
    setJoinBrowserStepId(stepId);
    setJoinBrowserOpen(true);
  }, []);
  // Every name the pipeline's Sources already use — one list across sheets,
  // uploaded files and step Output Names (buildSourceEntries in data.js).
  const sourceEntries = React.useMemo(
    () => buildSourceEntries({ sheets, secondaryDatasets, steps: pipeline.steps }),
    [sheets, secondaryDatasets, pipeline.steps],
  );
  // Last check before a pick is committed (FileBrowserModal's checkPick): a
  // name another kind of Source already holds blocks it; a name matching an
  // existing Source of the same kind is a replacement the user must confirm.
  const checkInputPick = React.useCallback((file) => {
    const names = (file.sheets || []).map(s => s.name);
    for (const name of names) {
      const owner = findSourceNameOwner(sourceEntries, name, { kind: 'sheet' });
      if (owner) return { blocked: sourceNameTakenMessage(name, owner) };
    }
    const existing = Array.from(sheets.keys());
    return { replaces: names.map(n => findSheetNameCollision(existing, n)).filter(Boolean) };
  }, [sourceEntries, sheets]);
  const checkJoinPick = React.useCallback((file) => {
    const savesData = (file.uploaded && file._parsedRows) || CATALOG_DATASETS[file.name];
    if (!savesData) return {};
    const owner = findSourceNameOwner(sourceEntries, file.name, { kind: 'file' });
    if (owner) return { blocked: sourceNameTakenMessage(file.name, owner) };
    const replaced = findSheetNameCollision(Array.from(secondaryDatasets.keys()), file.name);
    return { replaces: replaced ? [replaced] : [] };
  }, [sourceEntries, secondaryDatasets]);

  const onPickJoinFile = React.useCallback(async (file) => {
    const fileName = file.name;
    const data = file.uploaded && file._parsedRows
      ? { rows: file._parsedRows, columns: file._parsedColumns }
      : CATALOG_DATASETS[fileName];
    // A caller that wants the file selected as a Source needs real data behind
    // it — a mock-catalog entry has none, so say so instead of selecting a
    // name that doesn't exist.
    if (joinBrowserOnPickedRef.current && !(data && pipeline?.id)) {
      onToast?.({ kind: 'error', title: 'No data in that file', detail: `"${fileName}" is a placeholder catalog entry. Use the "Upload file" tab to bring in the real file.` });
      return;
    }
    // A file with real rows (uploaded, or a catalog entry that ships data) is
    // saved as a kind="file" Source. The server rejects a name another Source
    // already holds, so only update local state once the save has landed.
    if (data && pipeline?.id) {
      const rows = data.rows.map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i }));
      try {
        await api.upsertSheet(pipeline.id, fileName, { kind: 'file', rows, columns: data.columns, order: 0 });
      } catch (e) {
        onToast?.({ kind: 'error', title: 'File not added', detail: e.message });
        return;
      }
      setSecondaryDatasets(prev => new Map(prev).set(fileName, { rows, columns: data.columns, fileName }));
      setInputEpoch(n => n + 1);
    }
    // Point the step at this file: a caller-supplied field (Lookup's Source /
    // Driving Source), else the join step's config.right.
    if (joinBrowserOnPickedRef.current) {
      joinBrowserOnPickedRef.current(fileName);
      joinBrowserOnPickedRef.current = null;
    } else if (joinBrowserStepId) {
      setPipeline(p => ({
        ...p,
        steps: p.steps.map(s =>
          s.id === joinBrowserStepId
            ? { ...s, config: { ...s.config, right: fileName } }
            : s
        ),
      }));
    }
    setDirty(true);
    setJoinBrowserOpen(false);
  }, [joinBrowserStepId, pipeline?.id, onToast]);

  // Expose onReplaceInput to the top bar (rendered above ProjectScreen).
  React.useEffect(() => {
    if (window.__registerReplaceInput) window.__registerReplaceInput(onReplaceInput);
    return () => { if (window.__registerReplaceInput) window.__registerReplaceInput(null); };
  }, [onReplaceInput]);

  // Expose a Test/Prod setter so the top bar's env toggle can flip our
  // local pipeline state — which propagates back to the project tree.
  React.useEffect(() => {
    const setEnv = (env) => {
      setDirty(true);
      setPipeline(p => ({ ...p, env }));
    };
    if (window.__registerSetEnv) window.__registerSetEnv(setEnv);
    return () => { if (window.__registerSetEnv) window.__registerSetEnv(null); };
  }, [setDirty]);

  // Apply config change to a step
  const updateStep = (next) => {
    setDirty(true);
    setPipeline(p => ({
      ...p,
      steps: p.steps.map(s => s.id === next.id ? next : s),
    }));
    // Invalidate any cached test result for the step the user just edited.
    setTestResults(prev => {
      if (!prev.has(next.id)) return prev;
      const m = new Map(prev);
      m.delete(next.id);
      return m;
    });
  };

  // ── "Test this step" ────────────────────────────────────────────────
  // Asks the server to run just this step against the live upstream rows
  // and bring back the diff (cells changed, samples, row deltas). The whole
  // pipeline gets re-executed up to this step server-side — keeping the
  // local engine in sync isn't necessary.
  const runTest = React.useCallback(async (step) => {
    if (!step) return;
    if (!panels.preview) togglePanel('preview');
    const startedAt = Date.now();
    setTestResults(prev => {
      const m = new Map(prev);
      m.set(step.id, { status: 'running', startedAt });
      return m;
    });

    try {
      const result = await api.testStep(pipeline.id, {
        stepId: step.id,
        inputRows: pipeline.inputFile?.storagePath ? [] : uploadedData.rows,
        steps: pipeline.steps,
      });
      setTestResults(prev => {
        const m = new Map(prev);
        m.set(step.id, { ...result, finishedAt: Date.now() });
        return m;
      });
      setFlashTick(t => t + 1);
    } catch (err) {
      setTestResults(prev => {
        const m = new Map(prev);
        m.set(step.id, {
          status: 'error',
          durationMs: Date.now() - startedAt,
          error: (err && err.message) || String(err),
          finishedAt: Date.now(),
        });
        return m;
      });
    }
  }, [pipeline, panels.preview, togglePanel, uploadedData.rows]);

  const deleteStep = (id) => {
    setDirty(true);
    const target = pipeline.steps.find(s => s.id === id);
    // The step that was right after the deleted one (by order) — selecting
    // it automatically means repeated deletes don't need a re-select click
    // in between. Output always trails every pipeline, so this is always
    // found; the `?? null` is just defensive.
    const next = target && pipeline.steps.find(s => s.order === target.order + 1);
    setPipeline(p => {
      if (!target) return p;
      // Close the gap left behind: any step downstream of the removed one
      // shifts left by one slot and has its `order` decremented. Edges are
      // derived from the `order` chain, so the previous step automatically
      // reconnects to the next step — and the canvas tweens the slide-in.
      const GAP = 260;
      const closed = p.steps
        .filter(s => s.id !== id)
        .map(s => {
          let n = s;
          if (s.order > target.order) n = { ...n, order: n.order - 1 };
          if (s.x > target.x) n = { ...n, x: n.x - GAP };
          return n;
        });
      return { ...p, steps: closed };
    });
    if (selectedId === id) setSelectedId(next ? next.id : null);
  };

  // Drag-and-drop reorder: move step `id` to sit right after step `afterId`.
  // Steps keep the same set of slots (order/x/y), so source stays first and
  // output stays last; the canvas tweens each card into its new slot.
  const reorderStep = (id, afterId) => {
    const sorted = [...pipeline.steps].sort((a, b) => a.order - b.order);
    const moving = sorted.find(s => s.id === id);
    if (!moving || moving.type === 'source' || moving.type === 'output') return;
    const rest = sorted.filter(s => s.id !== id);
    const idx = rest.findIndex(s => s.id === afterId) + 1;
    if (idx < 1 || idx >= sorted.length) return;
    const next = [...rest.slice(0, idx), moving, ...rest.slice(idx)];
    if (next.every((s, i) => s.id === sorted[i].id)) return;
    const slots = sorted.map(s => ({ order: s.order, x: s.x, y: s.y }));
    const placed = new Map(next.map((s, i) => [s.id, slots[i]]));
    setDirty(true);
    setPipeline(p => ({
      ...p,
      steps: p.steps.map(s => (placed.has(s.id) ? { ...s, ...placed.get(s.id) } : s)),
    }));
  };

  const addStep = (type, afterId) => {
    setDirty(true);
    const sorted = [...pipeline.steps].sort((a, b) => a.order - b.order);
    const selected = sorted.find(s => s.id === (afterId ?? selectedId));

    // Choose the anchor the new node should sit right after:
    // - an explicit `afterId` (palette drag-drop onto the canvas, chosen by
    //   drop position) is always literal — insert right after that step,
    //   regardless of what's currently selected.
    // - a plain palette click (no afterId) inserts relative to the
    //   SELECTED step instead, to its LEFT — except Source, which nothing
    //   can precede, so a selected Source still inserts to its right; and
    //   except Output (or nothing selected), which falls back to the end
    //   of the pipeline, immediately before Output.
    let anchor;
    if (afterId != null) {
      anchor = selected;
    } else if (selected && selected.type === 'source') {
      anchor = selected;
    } else if (selected && selected.type !== 'output') {
      anchor = [...sorted].reverse().find(s => s.order < selected.order) || selected;
    } else {
      anchor = [...sorted].reverse().find(s => s.type !== 'output') || sorted[0];
    }

    const anchorOrder = anchor ? anchor.order : -1;
    const anchorX = anchor ? anchor.x : 0;
    const anchorY = anchor ? anchor.y : 140;
    const newOrder = anchorOrder + 1;
    const newX = anchorX + 260;

    const newStep = {
      id: 's_' + Math.random().toString(16).slice(2, 7),
      order: newOrder,
      type,
      x: newX,
      y: anchorY,
      label: STEP_TYPES[type].label,
      config: defaultConfigFor(type, uploadedData.columns),
    };

    // Shift order + x of every step at/after the insertion point so the new
    // node has clean room (the output always stays the rightmost node).
    const updatedSteps = pipeline.steps.map(s => {
      let next = s;
      if (s.order >= newOrder) next = { ...next, order: next.order + 1 };
      if (s.x >= newX) next = { ...next, x: next.x + 260 };
      return next;
    });

    setPipeline(p => ({ ...p, steps: [...updatedSteps, newStep] }));
    setSelectedId(newStep.id);

    // Flag the new step so the canvas plays its entrance, then clear it.
    setLastAddedStepId(newStep.id);
    if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
    lastAddedTimerRef.current = setTimeout(() => setLastAddedStepId(null), 1800);
  };

  // Built once, rendered inside whichever shell is active (floating or
  // docked to the canvas) — same props either way, so the two shells can't
  // drift out of sync with each other.
  const inspectorElement = (
    <StepInspector
      step={selectedStep}
      pipeline={pipeline}
      runMap={runMap}
      columns={inspectorColumns}
      originalColumns={originalColumns}
      uploadedRows={beforeRows}
      testResult={selectedStep ? testResults.get(selectedStep.id) : null}
      onChange={updateStep}
      onDelete={deleteStep}
      onTest={runTest}
      onReplaceInput={onReplaceInput}
      onChangeOutput={onChangeOutput}
      onBrowseFolder={onBrowseFolder}
      onBrowseJoinFile={onBrowseJoinFile}
      secondaryDatasets={secondaryDatasets}
      sheets={sheets}
      onToast={onToast}
      onRefreshSchema={onRefreshSchema}
      onDone={() => {
        // Close whichever window is showing the inspector.
        if (fullscreen === 'canvas' && fsInspectorOpen) setFsInspectorOpen(false);
        else if (panels.right) togglePanel('right');
      }}
      onClearTest={() => selectedStep && setTestResults(prev => {
        if (!prev.has(selectedStep.id)) return prev;
        const m = new Map(prev); m.delete(selectedStep.id); return m;
      })}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} data-screen-label={`Pipeline editor · ${pipeline.name}`}>
      <FileBrowserModal
        open={fileBrowserOpen}
        onClose={() => setFileBrowserOpen(false)}
        onPick={onPickInputFile}
        checkPick={checkInputPick}
        currentFile={pipeline.inputFile}
        currentPipelineId={pipeline.id}
        projects={projects}
        existingSheetNames={Array.from(sheets.keys())}
        sourceEntries={sourceEntries}
      />
      <FileBrowserModal
        open={joinBrowserOpen}
        onClose={() => setJoinBrowserOpen(false)}
        onPick={onPickJoinFile}
        checkPick={checkJoinPick}
        currentFile={joinBrowserStepId ? { name: pipeline.steps.find(s => s.id === joinBrowserStepId)?.config?.right } : null}
        currentPipelineId={pipeline.id}
        projects={projects}
        sourceEntries={sourceEntries}
      />
      <FolderPickerModal
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        onPick={onPickFolder}
        currentPath={pipeline.output?.folder}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left rail — outline + palette OR collapsed rail */}
        {!fullscreen && (panels.left ? (
          <StepOutline
            pipeline={pipeline}
            runMap={runMap}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAddStep={addStep}
            onCollapse={() => togglePanel('left')}
            width={leftWidth}
          />
        ) : (
          <CollapsedRail
            side="left"
            count={pipeline.steps.length}
            label="Add Items"
            onExpand={() => togglePanel('left')}
          />
        ))}
        {!fullscreen && panels.left && (
          <ResizeHandle
            axis="x"
            style={{ marginLeft: -4, marginRight: -4 }}
            onStart={() => { resizeStart.current = leftWidth; setResizing(true); }}
            onResize={(dx) => setLeftWidth(Math.max(200, Math.min(520, resizeStart.current + dx)))}
            onEnd={() => setResizing(false)}
          />
        )}

        {/* Center column: canvas (+ docked inspector, if docked) + preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {fullscreen !== 'preview' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <FlowCanvas
                  pipeline={pipeline}
                  stepsPerRow={pipeline.canvas?.stepsPerRow ?? DEFAULT_STEPS_PER_ROW}
                  onStepsPerRowChange={(n) => setPipeline(p => ({ ...p, canvas: { ...(p.canvas || {}), stepsPerRow: n } }))}
                  runMap={runMap}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  density={density}
                  isFullscreen={fullscreen === 'canvas'}
                  onOpenStep={fullscreen === 'canvas' ? (id) => { setSelectedId(id); setFsInspectorOpen(true); } : undefined}
                  onToggleFullscreen={() => toggleFullscreen('canvas')}
                  lastAddedStepId={lastAddedStepId}
                  onDropStepType={addStep}
                  onReorderStep={reorderStep}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={undo}
                  onRedo={redo}
                  columnFocus={columnFocus}
                  onClearColumnFocus={() => setFocusColumn(null)}
                />
              </div>
              {!fullscreen && panels.right && inspectorDocked && selectedStep && (
                <>
                  <ResizeHandle
                    axis="x"
                    style={{ marginLeft: -4, marginRight: -4 }}
                    onStart={() => { resizeStart.current = dockedWidth; setResizing(true); }}
                    onResize={(dx) => setDockedWidth(Math.max(320, Math.min(720, resizeStart.current - dx)))}
                    onEnd={() => setResizing(false)}
                  />
                  <DockedInspectorPanel
                    width={dockedWidth}
                    onUndock={() => dockInspector(false)}
                    onClose={() => togglePanel('right')}
                  >
                    {inspectorElement}
                  </DockedInspectorPanel>
                </>
              )}
            </div>
          )}
          {fullscreen !== 'canvas' && panels.preview && fullscreen !== 'preview' && (
            <ResizeHandle
              axis="y"
              style={{ marginTop: -4, marginBottom: -4 }}
              onStart={() => { resizeStart.current = previewHeight; setResizing(true); }}
              onResize={(_dx, dy) => setPreviewHeight(Math.max(120, Math.min(window.innerHeight - 260, resizeStart.current - dy)))}
              onEnd={() => setResizing(false)}
            />
          )}
          {fullscreen !== 'canvas' && (
            panels.preview || fullscreen === 'preview' ? (
              <DataPreview
                resizing={resizing}
                beforeRows={previewBeforeRows}
                afterRows={previewAfterRows}
                columns={previewColumns}
                step={previewStep}
                scope={previewScope}
                onScopeChange={setPreviewScope}
                modifiedColumns={modifiedColumns}
                savedInputRows={(!uploadedData.fileName && pipeline.inputFile?.rows) ? pipeline.inputFile.rows : null}
                collapsed={false}
                onToggleCollapse={() => togglePanel('preview')}
                height={fullscreen === 'preview' ? Infinity : previewHeight}
                flashTick={flashTick}
                isFullscreen={fullscreen === 'preview'}
                onToggleFullscreen={() => toggleFullscreen('preview')}
                sources={previewSources}
                activeSource={activeSource}
                onSelectSource={setActiveSheet}
                stepInfo={previewStep ? runMap.get(previewStep.id) : null}
                previewPending={previewPending}
                onDownload={(format) => onDownloadStep(previewStep, format)}
                columnFids={columnFids}
                focusFid={focusColumn?.fid || null}
                onPickColumn={onPickColumn}
              />
            ) : (
              <CollapsedPreviewBar
                step={selectedStep}
                info={selectedStep ? runMap.get(selectedStep.id) : null}
                onExpand={() => togglePanel('preview')}
              />
            )
          )}
        </div>

      </div>

      {/* Configuration panel — floating (draggable anywhere, default) or
          docked to the right of the canvas (see the docked render above,
          inside the center column) — the "Dock"/"Undock" button on each
          shell switches between them. Requires both `panels.right` (its
          own "×"/collapse state) AND a selected step — clicking empty
          canvas to deselect closes this window rather than showing
          StepInspector's "Nothing selected" placeholder. `panels.right`
          itself is left untouched on deselect, so re-selecting a step
          reopens the panel without the auto-reopen effect above having to
          fire. */}
      {!fullscreen && panels.right && !inspectorDocked && selectedStep && (
        <FloatingInspectorPanel onClose={() => togglePanel('right')} onDock={() => dockInspector(true)}>
          {inspectorElement}
        </FloatingInspectorPanel>
      )}
      {fullscreen === 'canvas' && fsInspectorOpen && selectedStep && (
        <FloatingInspectorPanel onClose={() => setFsInspectorOpen(false)}>
          {inspectorElement}
        </FloatingInspectorPanel>
      )}
    </div>
  );
}

// ── Floating, draggable configuration panel ─────────────────────────────
// Replaces the old docked right-hand inspector column. Renders over the
// workspace with a transparent surrounding backdrop (it's just an
// absolutely-positioned card, no overlay/scrim element at all) so the
// canvas and data preview underneath stay fully visible and interactive.
// Position persists across re-renders (but not across unmount — a fresh
// panel starts back at the default top-right slot) via plain useState;
// dragging is implemented by hand (no external dependency) since it's a
// small, self-contained drag-one-box-around behavior.
function FloatingInspectorPanel({ children, onClose, onDock }) {
  const panelRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [pos, setPos] = React.useState(null); // null = default top-right slot
  // 'normal' | 'maximized' | 'minimized' — a single enum rather than two
  // independent booleans, so clicking maximize while minimized (or vice
  // versa) just switches state instead of needing special-case handling.
  const [windowState, setWindowState] = React.useState('normal');
  const [size, setSize] = React.useState(null); // null = default 480 × min(88vh, 820px)
  const sizeStart = React.useRef(null);
  const isMax = windowState === 'maximized';
  const isMin = windowState === 'minimized';

  React.useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const panel = panelRef.current;
      const w = panel?.offsetWidth || 480;
      const h = panel?.offsetHeight || 600;
      const vw = window.innerWidth, vh = window.innerHeight;
      const left = Math.max(4, Math.min(d.origLeft + (e.clientX - d.startX), vw - w - 4));
      const top = Math.max(4, Math.min(d.origTop + (e.clientY - d.startY), vh - h - 4));
      setPos({ left, top });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const onDragHandleDown = (e) => {
    if (isMax) return; // dragging a maximized window doesn't make sense
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origLeft: rect.left, origTop: rect.top };
  };

  const positionStyle = pos
    ? { left: pos.left, top: pos.top }
    : { top: 76, right: 20 };

  const sizeStyle = isMax
    ? { left: 8, top: 8, right: 'auto', width: 'calc(100vw - 16px)', height: 'calc(100vh - 16px)' }
    : { ...positionStyle, width: size?.w ?? 480, height: isMin ? undefined : (size?.h ?? 'min(88vh, 820px)') };

  // Resize from any edge/corner. First use pins the window to explicit
  // left/top so growing/shrinking works from the left edge too.
  const beginResize = () => {
    const r = panelRef.current.getBoundingClientRect();
    sizeStart.current = { l: r.left, t: r.top, w: r.width, h: r.height };
    setPos({ left: r.left, top: r.top });
    setSize({ w: r.width, h: r.height });
  };
  const applyResize = (dx, dy, left, top) => {
    const st = sizeStart.current;
    if (!st) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    let { l, t, w, h } = st;
    if (left) { w = Math.max(320, Math.min(st.w - dx, st.l + st.w - 4)); l = st.l + st.w - w; }
    else if (dx) { w = Math.max(320, Math.min(st.w + dx, vw - st.l - 4)); }
    if (top) { h = Math.max(240, Math.min(st.h - dy, st.t + st.h - 4)); t = st.t + st.h - h; }
    else if (dy) { h = Math.max(240, Math.min(st.h + dy, vh - st.t - 4)); }
    setPos({ left: l, top: t });
    setSize({ w, h });
  };

  return (
    <div
      ref={panelRef}
      data-inspector-max={isMax ? 'true' : undefined}
      style={{
        position: 'fixed', ...sizeStyle,
        background: C.bar, border: `1px solid ${C.borderStrong}`,
        borderRadius: 12, boxShadow: '0 20px 56px rgba(0,0,0,.45)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        zIndex: 2000,
      }}
    >
      {/* Drag handle strip — the only part of this window that isn't the
          inspector's own content, so grabbing anywhere on it (except the
          buttons on the right) repositions the whole panel. */}
      <div
        onMouseDown={onDragHandleDown}
        style={{
          flexShrink: 0, cursor: isMax ? 'default' : 'grab',
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 8px 6px 12px',
          background: C.surface, borderBottom: `1px solid ${C.border}`,
          userSelect: 'none',
        }}
      >
        <span style={{ color: C.fg5, fontSize: '.8rem', letterSpacing: '2px', flex: 1 }}>⋮⋮⋮⋮⋮⋮⋮⋮⋮⋮⋮⋮⋮⋮</span>
        {onDock && (
          <IconButton title="Dock to the canvas" onClick={onDock}><PanelRightOpen size={14} /></IconButton>
        )}
        <IconButton
          title={isMin ? 'Restore' : 'Minimize'}
          onClick={() => setWindowState(isMin ? 'normal' : 'minimized')}
        >{isMin ? <Square size={12} /> : <Minus size={14} />}</IconButton>
        <IconButton
          title={isMax ? 'Restore' : 'Maximize'}
          onClick={() => setWindowState(isMax ? 'normal' : 'maximized')}
        >{isMax ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</IconButton>
        <IconButton title="Close" onClick={onClose}><X size={14} /></IconButton>
      </div>
      {!isMin && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {children}
        </div>
      )}
      {!isMax && (
        <>
          <ResizeHandle axis="x" onStart={beginResize} onResize={(dx) => applyResize(dx, 0, true)}
            style={{ position: 'absolute', left: 0, top: 36, bottom: 12 }} />
          <ResizeHandle axis="x" onStart={beginResize} onResize={(dx) => applyResize(dx, 0, false)}
            style={{ position: 'absolute', right: 0, top: 36, bottom: 12 }} />
          {!isMin && (
            <>
              <ResizeHandle axis="y" onStart={beginResize} onResize={(_dx, dy) => applyResize(0, dy, false, false)}
                style={{ position: 'absolute', left: 12, right: 12, bottom: 0 }} />
              <ResizeHandle axis="both" onStart={beginResize} onResize={(dx, dy) => applyResize(dx, dy, false, false)}
                style={{ position: 'absolute', right: 0, bottom: 0 }} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Docked configuration panel ───────────────────────────────────────
// The "Dock to the canvas" alternative to FloatingInspectorPanel above: a
// plain flex column sitting to the right of FlowCanvas (its parent row in
// PipelineEditor), not a fixed-position overlay — so it takes real layout
// space from the canvas, but Data Preview below (a sibling of that row,
// not a child of it) is never affected and always stays full width.
// No drag/minimize/maximize — those only make sense for a floating window;
// width is adjustable via the ResizeHandle the caller places before it.
function DockedInspectorPanel({ children, width, onUndock, onClose }) {
  return (
    <div style={{
      width, flexShrink: 0,
      background: C.bar, borderLeft: `1px solid ${C.borderStrong}`,
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
        padding: '6px 8px', background: C.surface, borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{ flex: 1 }} />
        <IconButton title="Undock (float anywhere)" onClick={onUndock}><PanelRightClose size={14} /></IconButton>
        <IconButton title="Close" onClick={onClose}><X size={14} /></IconButton>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

// ── Collapsed rail (vertical 28px bar shown when left/right is hidden) ─
function CollapsedRail({ side, label, icon, count, onExpand }) {
  const isLeft = side === 'left';
  return (
    <div
      onClick={onExpand}
      title={`Show ${label}`}
      style={{
        width: 32, flexShrink: 0,
        background: C.bar,
        borderRight: isLeft ? `1px solid ${C.borderStrong}` : 'none',
        borderLeft: !isLeft ? `1px solid ${C.borderStrong}` : 'none',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '14px 0', gap: 14, cursor: 'pointer',
        transition: 'background-color .15s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = C.surface}
      onMouseLeave={(e) => e.currentTarget.style.background = C.bar}
    >
      <span style={{
        color: C.fg4, fontSize: '.9rem',
        transform: isLeft ? 'rotate(0deg)' : 'rotate(180deg)',
      }}>{isLeft ? '›' : '›'}</span>
      {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      <span style={{
        color: C.fg4, fontSize: '.7rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '.1em',
        writingMode: 'vertical-rl', transform: 'rotate(180deg)',
        whiteSpace: 'nowrap',
      }}>{label}</span>
      {count != null && (
        <span style={{
          color: C.accentBright, fontSize: '.66rem', fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
          padding: '1px 5px', borderRadius: 8,
        }}>{count}</span>
      )}
    </div>
  );
}

// ── Collapsed preview bar (thin horizontal strip when preview is hidden) ─
function CollapsedPreviewBar({ step, info, onExpand }) {
  return (
    <div
      onClick={onExpand}
      title="Show data preview"
      style={{
        height: 32, flexShrink: 0,
        borderTop: `1px solid ${C.borderStrong}`,
        background: C.bar,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 14px', cursor: 'pointer',
        transition: 'background-color .15s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = C.surface}
      onMouseLeave={(e) => e.currentTarget.style.background = C.bar}
    >
      <span style={{ color: C.fg4, fontSize: '.85rem' }}>▴</span>
      <span style={{
        color: C.fg2, fontSize: '.78rem', fontWeight: 600,
      }}>Data preview</span>
      <span style={{ width: 1, height: 14, background: C.border }} />
      <span style={{ color: C.fg5, fontSize: '.74rem' }}>
        {step ? (step.label || summarizeStep(step)) : 'No step selected'}
      </span>
      {info && (
        <>
          <div style={{ flex: 1 }} />
          <span style={{
            color: C.fg4, fontSize: '.72rem',
            fontFamily: "'JetBrains Mono', monospace",
          }}>{info.inputCount} → {info.outputCount}</span>
          {info.delta !== 0 && (
            <span style={{
              color: info.delta < 0 ? C.warn : C.ok,
              fontSize: '.72rem', fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
            }}>{info.delta > 0 ? '+' : ''}{info.delta}</span>
          )}
        </>
      )}
    </div>
  );
}

// Live schema from preview rows. Known source columns keep their original
// type badges; keys the step added (e.g. mrr_scaled) are inferred.
function columnsFromRows(rows, baseColumns = []) {
  const baseByName = new Map((baseColumns || []).map(c => [c.name, c]));
  if (!rows || !rows.length) return baseColumns || [];
  // Look at every row the preview can show (it holds at most 200), so a
  // column a gated step wrote only on later rows still gets a header.
  const inferred = inferColumns(rows, 200);
  if (!inferred.length) return baseColumns || [];
  return inferred.map(c => baseByName.get(c.name) || c);
}

function defaultConfigFor(type, columns = SOURCE_COLUMNS) {
  const firstCol = columns[0]?.name;
  // `__rowIndex` is a purely synthetic per-row index (never real data) —
  // never default a scaling step onto it. `id` is not excluded: it's
  // commonly a genuine numeric business column, not just App's row-key.
  const firstNumeric = columns.find(c => c.type === 'number' && c.name !== '__rowIndex')?.name;
  switch (type) {
    // Ingestion
    case 'source_url': return { url: 'https://example.com/data.csv' };
    case 'source_cloud': return { uri: 's3://my-bucket/path/file.csv' };
    // Cleaning
    case 'dedupe': return { columns: firstCol ? [firstCol] : [] };
    case 'drop_nulls': return { columns: [] };
    case 'fill_nulls': return { column: firstCol, method: 'constant', fill: 'UNKNOWN' };
    case 'trim': return { columns: [], internal: 'no' };
    case 'case': return { column: firstCol, mode: 'lower' };
    case 'strip_chars': return { column: firstCol, chars: '.,;:!?' };
    case 'left_trim': return { columns: [] };
    case 'right_trim': return { columns: [] };
    case 'mid': return { column: firstCol, start: 0, length: 10 };
    case 'find_replace': return { column: firstCol, find: '', replace: '', caseSensitive: true };
    // Type & parse
    case 'cast_type': return { column: firstCol, target: 'string', onError: 'null' };
    case 'parse_date': return { column: 'signup', format: 'ISO', tz: 'utc' };
    case 'parse_number': return { column: 'mrr', decimal: '.' };
    case 'parse_bool': return { column: firstCol };
    // Row transforms
    case 'filter': return { mode: 'drop', conjunction: 'AND', conditions: [{ column: 'status', op: 'eq', value: 'cancelled' }], output_name: '' };
    case 'compute': return { target: 'computed', expression: `UPPER({${firstCol}})`, source: '__self__', output_name: '' };
    case 'case_when': return { target: 'result', else: 'other', cases: [{ when: [{ column: firstCol, op: 'eq', value: '' }], then: '' }], source: '__self__', output_name: '' };
    case 'regex_extract': return { column: 'email', pattern: '@(.+)$', target: 'email_domain' };
    case 'hash': return { column: 'email', mode: 'mask' };
    case 'lookup': return { column: 'country', target: 'country', map: { US: 'United States', JP: 'Japan' }, unmapped: 'keep' };
    // Columns
    case 'rename': return { renames: {} };
    case 'drop_columns': return { columns: [], source: '__self__', output_name: '' };
    case 'reorder': return { order: columns.map(c => c.name), source: '__self__', output_name: '' };
    case 'split_column': return { column: 'email', delim: '@', into: ['local_part', 'domain'] };
    case 'merge_columns': return { columns: [], sep: ' · ', target: 'merged' };
    // Sort & rank
    case 'sort': return { keys: [{ column: firstCol, dir: 'asc' }] };
    case 'top_n': return { n: 10, from: 'top' };
    // Aggregate
    case 'group_by': return { keys: [], aggs: [], source: '__self__', output_name: '' };
    case 'window': return { fn: 'row_number', partition: ['country'], orderBy: 'mrr', target: 'rank' };
    case 'conditional_group': return { target: 'GroupCode', keys: [], order: [], breaks: [], numbering: 'global' };
    case 'rolling': return { column: 'mrr', fn: 'avg', window: 7 };
    // Join
    case 'join': return { right: 'orders.csv', kind: 'inner', keys: [{ left: 'id', right: 'customer_id' }], rightPrefix: '' };
    case 'union': return { right: 'customers_eu.csv', align: 'name', mismatch: 'fill', left_source: '__self__', right_source: '__self__', output_name: '' };
    case 'fuzzy_match': return { right: 'vendor_master.csv', column: 'company', rightCol: 'vendor_name', algo: 'jaro_winkler', threshold: 0.85 };
    // Reshape
    case 'pivot': return { index: 'country', columns_pivot: 'plan', values: 'mrr', agg: 'sum' };
    case 'unpivot': return { id_vars: [], var_name: 'metric', value_name: 'value', source: '__self__', output_name: '' };
    case 'explode': return { column: firstCol, empty: 'drop' };
    // Validate
    case 'regex_validate': return { column: 'email', pattern: '^[\\w.+-]+@[\\w-]+\\.[\\w.-]+$', action: 'flag' };
    case 'range_check': return { column: 'mrr', min: '0', max: '10000', action: 'drop' };
    case 'assert': return { kind: 'rowcount', min: '1', max: '', note: 'Row count should be > 0', severity: 'halt' };
    // Enrich
    case 'geocode': return { column: 'country', provider: 'mapbox', latCol: 'lat', lonCol: 'lon' };
    case 'currency': return { column: 'mrr', from: 'USD', to: 'EUR' };
    case 'http_request': return {
      method: 'POST', url: '', params: [], headers: [], auth_type: 'none',
      body_type: 'records', fields: [], field_keys: {}, body_template: '', single_object: false,
      batch_size: 10, timeout_s: 30, delay_ms: 0, on_error: 'continue',
      status_column: 'http_status', response_column: 'http_response',
      response_to_columns: false, response_path: '', response_prefix: '',
      response_match_column: '', response_match_field: '',
      poll: false, poll_url_field: 'resultUrl', poll_status_field: 'status',
      poll_done_values: 'completed, failed', poll_success_values: 'completed', poll_interval_s: 2, poll_max_wait_s: 300,
    };
    case 'api_lookup': return { method: 'GET', url: 'https://api.example.com/lookup/{email}', auth: 'bearer', rate: 10 };
    case 'sentiment': return { column: firstCol, model: 'vader', scoreCol: 'sentiment_score', labelCol: 'sentiment_label' };
    case 'classify': return { column: firstCol, strategy: 'rules', classes: 'enterprise, growth, starter', target: 'class' };
    // Sample / split
    case 'sample': return { mode: 'random', unit: 'n', n: 1000, seed: '42' };
    case 'split_dataset': return { ratio: 0.8, seed: '42', target: 'split' };
    // Profile
    case 'profile_columns': return { columns: [] };
    case 'profile_outliers': return { column: 'mrr', method: 'iqr', target: 'is_outlier' };
    // Feature Engineering
    case 'min_max_scaling': return { columns: firstNumeric ? [firstNumeric] : [], range: '0,1', output_column: '' };
    case 'standard_scaling': return { columns: firstNumeric ? [firstNumeric] : [], output_column: '' };
    case 'robust_scaling': return { columns: firstNumeric ? [firstNumeric] : [], output_column: '' };

    case 'ordinal_encoding': return { columns: firstCol ? [firstCol] : [], output_column: '' };
    case 'one_hot_encoding': return { columns: firstCol ? [firstCol] : [], output_column: '' };
    case 'frequency_encoding': return { columns: firstCol ? [firstCol] : [], output_column: '' };

    case 'fixed_clipping': return { columns: firstNumeric ? [firstNumeric] : [], action: 'clip', lower_bound: '', upper_bound: '', output_column: '' };
    case 'percentile_bounds': return { columns: firstNumeric ? [firstNumeric] : [], action: 'clip', lower_quantile: 0.01, upper_quantile: 0.99, output_column: '' };
    case 'iqr_outlier': return { columns: firstNumeric ? [firstNumeric] : [], action: 'clip', k: 1.5, output_column: '' };
    case 'zscore_outlier': return { columns: firstNumeric ? [firstNumeric] : [], action: 'clip', z: 3.0, output_column: '' };

    case 'variance_threshold': return { columns: firstNumeric ? [firstNumeric] : [], threshold: 0.0, manual_overrides: {} };
    case 'pearson_correlation': return { target: firstCol || '', threshold: 0.05, manual_overrides: {} };
    case 'spearman_correlation': return { target: firstCol || '', threshold: 0.05, manual_overrides: {} };
    case 'kendall_correlation': return { target: firstCol || '', threshold: 0.05, manual_overrides: {} };
    case 'chi2_selection': return { target: firstCol || '', max_p_value: 0.05, manual_overrides: {} };
    case 'anova_selection': return { target: firstCol || '', max_p_value: 0.05, manual_overrides: {} };
    
    case 'text_structural_metrics': return { columns: [], metrics: ['word_count', 'char_count', 'sentence_count', 'avg_word_length'], suffix: '_{metric}', null_handling: 'zero' };

    case 'skew_correction_step': return { column: firstNumeric || '', method: 'log1p', suffix: '_transformed', output_column: '' };

    default: return {};
  }
}

// ── App root ───────────────────────────────────────────────────────────
// The mock email login (any email signs in) is only offered at this URL; the
// app's normal URL signs in through RAPFlow SSO.
const MOCK_LOGIN_PATH = '/mock-login';
const isMockLoginPath = () => window.location.pathname.replace(/\/+$/, '') === MOCK_LOGIN_PATH;

function App() {
  // view: 'projects' (list of all projects) | 'pipelines' (list of pipelines in a project) | 'pipeline' (flow editor)
  const [view, setView] = React.useState('projects');
  const [projectId, setProjectId] = React.useState(null);
  const [pipelineId, setPipelineId] = React.useState(null);
  // Project catalog — hydrated from the backend on mount and after login.
  const [projects, setProjects] = React.useState([]);
  const [editingProject, setEditingProject] = React.useState(null); // project in the edit dialog
  // Organization catalog — drives the top-bar org picker.
  const [orgs, setOrgs] = React.useState([]);
  const [newProjectOpen, setNewProjectOpen] = React.useState(false);
  const [schedulePipeline, setSchedulePipeline] = React.useState(null);
  const [user, setUser] = React.useState(null);
  // `null` while we're still resolving the session via /api/me. Prevents the
  // login screen from flashing for already-authenticated visitors.
  const [bootstrapped, setBootstrapped] = React.useState(false);
  // Why the last SSO attempt failed, shown on the SSO sign-in screen.
  const [ssoError, setSsoError] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  React.useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(id);
  }, [toast]);
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [runOpen, setRunOpen] = React.useState(false);
  const [runsOpen, setRunsOpen] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [lastRunId, setLastRunId] = React.useState(null);
  // Run history is in-memory only until Phase 4 wires it to the server.
  const [runs, setRuns] = React.useState([]);
  const [uploadedData, setUploadedData] = React.useState(
    { rows: SOURCE_ROWS, columns: SOURCE_COLUMNS, fileName: null }
  );

  // ── Bootstrap session + catalog ───────────────────────────────────
  // On first paint, ask the backend who we are. If a cookie is valid, load
  // projects + orgs in parallel. A 401 just means "show the login screen".
  const reloadCatalog = React.useCallback(async () => {
    try {
      const [pr, og] = await Promise.all([api.listProjects(), api.listOrgs()]);
      setProjects(pr);
      setOrgs(og);
    } catch (e) {
      setToast({ kind: 'error', title: 'Couldn’t load workspace', detail: e.message });
    }
  }, []);
  React.useEffect(() => {
    (async () => {
      // Single sign-on: RAPFlow opens the app at ?token=<its access token>.
      // Take the token out of the address bar first so it doesn't stay in
      // history, then exchange it for a session. A failed SSO shows the
      // login screen rather than silently reusing an older session.
      const url = new URL(window.location.href);
      const ssoToken = url.searchParams.get('token');
      if (ssoToken) {
        url.searchParams.delete('token');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        try {
          const { user: u } = await api.ssoLogin(ssoToken);
          setUser(u);
          await reloadCatalog();
        } catch (e) {
          setSsoError(e.message);
        } finally {
          setBootstrapped(true);
        }
        return;
      }
      try {
        const u = await api.me();
        setUser(u);
        await reloadCatalog();
      } catch (e) {
        if (e?.status && e.status !== 401) {
          setToast({ kind: 'error', title: 'Server unreachable', detail: e.message, detailMono: true });
        }
      } finally {
        setBootstrapped(true);
      }
    })();
  }, [reloadCatalog]);

  // ProjectScreen registers a handler here so the top-bar's Replace input
  // button can open its file browser modal.
  const replaceInputRef = React.useRef(null);
  React.useEffect(() => {
    window.__registerReplaceInput = (fn) => { replaceInputRef.current = fn; };
    return () => { delete window.__registerReplaceInput; };
  }, []);

  // Same pattern for the top-bar's Test/Prod toggle while the pipeline
  // editor is open — PipelineEditor registers a setter that touches its
  // local pipeline state (which then syncs back to the project tree).
  const setEnvRef = React.useRef(null);
  React.useEffect(() => {
    window.__registerSetEnv = (fn) => { setEnvRef.current = fn; };
    return () => { delete window.__registerSetEnv; };
  }, []);

  // Toggle a pipeline's publish env from outside the editor (used on the
  // pipelines list). Optimistic update, then server PATCH.
  const setPipelineEnv = async (targetPipelineId, env) => {
    setProjects(prev => prev.map(pr => ({
      ...pr,
      pipelines: pr.pipelines.map(pl => pl.id !== targetPipelineId ? pl : { ...pl, env }),
    })));
    try {
      await api.updatePipeline(targetPipelineId, { env });
    } catch (e) {
      setToast({ kind: 'error', title: 'Save failed', detail: e.message });
      await reloadCatalog();
    }
  };

  // Theme: 'dark' (default) or 'light'. The preference is purely a client-side
  // UI choice — no need to round-trip through the backend — so we cache it in
  // localStorage. The matching <html data-theme="..."> attribute is set in an
  // effect below, which means even before React mounts the dark palette is
  // already on screen (no flash for new visitors).
  const [theme, setTheme] = React.useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('dpstudio.theme') === 'light' ? 'light' : 'dark';
  });
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { window.localStorage.setItem('dpstudio.theme', theme); } catch { /* quota */ }
  }, [theme]);
  const onToggleTheme = React.useCallback(() => {
    setTheme(t => t === 'light' ? 'dark' : 'light');
  }, []);

  // Which side panels are visible in the project editor.
  const [panels, setPanels] = React.useState({ left: true, right: true, preview: true });
  const togglePanel = React.useCallback((which) => {
    setPanels(p => ({ ...p, [which]: !p[which] }));
  }, []);

  // Save handler — explicit "checkpoint" affordance. Each mutation already
  // round-trips to the server; this is purely a visible confirmation.
  const onSave = React.useCallback(() => {
    setDirty(false);
    const proj = projects.find(p => p.id === projectId);
    const pip = proj?.pipelines.find(pl => pl.id === pipelineId);
    setToast({ kind: 'ok', title: 'Saved', detail: pip?.name || proj?.name });
  }, [projects, projectId, pipelineId]);

  // Keyboard shortcuts: ⌥1 / ⌥2 / ⌥3
  React.useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey) return;
      if (e.key === '1') { e.preventDefault(); togglePanel('left'); }
      else if (e.key === '2') { e.preventDefault(); togglePanel('preview'); }
      else if (e.key === '3') { e.preventDefault(); togglePanel('right'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePanel]);

  const project = projects.find(p => p.id === projectId);
  const pipeline = project?.pipelines.find(pl => pl.id === pipelineId);

  // ── Navigation ────────────────────────────────────────────────
  const onOpenProject = (p) => {
    setProjectId(p.id);
    setView('pipelines');
    setPipelineId(null);
    setDirty(false);
  };
  const onOpenPipeline = (pl) => {
    setPipelineId(pl.id);
    setView('pipeline');
    setDirty(false);
  };
  const onBackToPipelines = () => {
    if (!project) return;
    setView('pipelines');
    setPipelineId(null);
  };
  const onHome = () => {
    setView('projects');
    setProjectId(null);
    setPipelineId(null);
  };

  // Create a new project from the modal and open it.
  const createProject = async (spec) => {
    try {
      const created = await api.createProject({
        name: spec.name,
        description: spec.description || '',
        icon: spec.icon || '✦',
      });
      setProjects(prev => [created, ...prev]);
      setNewProjectOpen(false);
      setProjectId(created.id);
      setView('pipelines');
    } catch (e) {
      setToast({ kind: 'error', title: 'Create failed', detail: e.message });
    }
  };

  // ── Import / export ────────────────────────────────────────────────
  // We accept either a raw `{...}` document or a wrapped `{ project: ... }` /
  // `{ pipeline: ... }` envelope (matching what we emit). The server is the
  // source of truth, so an import becomes a Create followed by per-pipeline
  // configuration writes.
  const importProject = () => {
    pickJSONFile(async (data, filename, err) => {
      if (!data) {
        setToast({ kind: 'error', title: 'Import failed', detail: err?.message || 'Invalid JSON', detailMono: true });
        return;
      }
      const raw = data.project || data;
      if (!raw || !raw.name) {
        setToast({ kind: 'error', title: 'Not a project file', detail: filename, detailMono: true });
        return;
      }
      try {
        const created = await api.createProject({
          name: raw.name + (raw.name.endsWith(' (imported)') ? '' : ' (imported)'),
          description: raw.description || '',
          icon: raw.icon || '✦',
        });
        // Recreate pipelines one by one — server assigns fresh IDs.
        for (const pl of (raw.pipelines || [])) {
          const fresh = await api.createPipeline(created.id, {
            name: pl.name,
            description: pl.description || '',
            mode: 'blank',
          });
          await api.updatePipeline(fresh.id, {
            env: pl.env,
            status: pl.status,
            schedule: pl.schedule,
            inputFile: pl.inputFile,
            output: pl.output,
            canvas: pl.canvas,
            steps: resetReviewOnImport(pl.steps || []).map((s, i) => ({
              order: s.order ?? i, type: s.type, x: s.x ?? 0, y: s.y ?? 0,
              label: s.label || s.type, disabled: !!s.disabled, config: s.config || {},
            })),
          });
        }
        await reloadCatalog();
        setProjectId(created.id);
        setView('pipelines');
        setToast({
          kind: 'ok',
          title: 'Project imported',
          detail: `${created.name} · ${(raw.pipelines || []).length} pipeline${(raw.pipelines || []).length === 1 ? '' : 's'}`,
        });
      } catch (e2) {
        setToast({ kind: 'error', title: 'Import failed', detail: e2.message });
      }
    });
  };
  const importPipeline = () => {
    if (!projectId) return;
    pickJSONFile(async (data, filename, err) => {
      if (!data) {
        setToast({ kind: 'error', title: 'Import failed', detail: err?.message || 'Invalid JSON', detailMono: true });
        return;
      }
      const raw = data.pipeline || data;
      if (!raw || !raw.name || !Array.isArray(raw.steps)) {
        setToast({ kind: 'error', title: 'Not a pipeline file', detail: filename, detailMono: true });
        return;
      }
      try {
        const fresh = await api.createPipeline(projectId, {
          name: raw.name + (raw.name.endsWith(' (imported)') ? '' : ' (imported)'),
          description: raw.description || '',
          mode: 'blank',
        });
        await api.updatePipeline(fresh.id, {
          env: raw.env, status: raw.status || 'draft', schedule: raw.schedule,
          // inputFile is deliberately not carried over: its storagePath points at
          // the ORIGINAL pipeline's stored file, which this new pipeline doesn't have.
          output: raw.output,
          canvas: raw.canvas,
          // Imported code expressions are never pre-approved: reviewed is reset
          // to false whatever the file says (Case When code mode).
          steps: resetReviewOnImport(raw.steps).map((s, i) => ({
            order: s.order ?? i, type: s.type, x: s.x ?? 0, y: s.y ?? 0,
            label: s.label || s.type, disabled: !!s.disabled, config: s.config || {},
          })),
        });
        await reloadCatalog();
        setToast({ kind: 'ok', title: 'Pipeline imported (steps only)', detail: `${raw.name} — upload the input file and any sheets again` });
      } catch (e2) {
        setToast({ kind: 'error', title: 'Import failed', detail: e2.message });
      }
    });
  };
  const exportProject = (proj) => {
    if (!proj) return;
    const payload = {
      $schema: 'dpipe-project',
      version: 1,
      exportedAt: new Date().toISOString(),
      project: proj,
    };
    downloadJSON(`project-${slugify(proj.name)}.json`, payload);
    setToast({
      kind: 'ok',
      title: 'Project exported',
      detail: `project-${slugify(proj.name)}.json`,
      detailMono: true,
    });
  };
  const exportPipeline = (pl) => {
    if (!pl) return;
    const payload = {
      $schema: 'dpipe-pipeline',
      version: 1,
      exportedAt: new Date().toISOString(),
      pipeline: pl,
    };
    downloadJSON(`pipeline-${slugify(pl.name)}.json`, payload);
    setToast({
      kind: 'ok',
      title: 'Pipeline exported',
      detail: `pipeline-${slugify(pl.name)}.json`,
      detailMono: true,
    });
  };

  // ── Pipeline mutations (create / duplicate / pause / delete / save edits) ──
  // Whole-pipeline PUT — the editor sends back the complete pipeline on each
  // local edit. We mirror to React state immediately (so the UI stays snappy)
  // and debounce the network write by 200ms so a burst of keystrokes collapses
  // to one save. Without this, concurrent PUTs raced against the server's
  // delete-then-create step replace and could leave duplicated steps in the
  // DB. The server now wraps that block in a transaction too — belt + braces.
  const saveTimersRef = React.useRef(new Map()); // pipelineId → timeout id
  const saveLatestRef = React.useRef(new Map()); // pipelineId → latest snapshot
  const updatePipeline = React.useCallback((nextPipeline) => {
    setProjects(prev => prev.map(pr => pr.id !== projectId ? pr : {
      ...pr,
      pipelines: pr.pipelines.map(pl => pl.id === nextPipeline.id ? nextPipeline : pl),
    }));
    saveLatestRef.current.set(nextPipeline.id, nextPipeline);
    const existing = saveTimersRef.current.get(nextPipeline.id);
    if (existing) clearTimeout(existing);
    const tid = setTimeout(async () => {
      saveTimersRef.current.delete(nextPipeline.id);
      const latest = saveLatestRef.current.get(nextPipeline.id);
      saveLatestRef.current.delete(nextPipeline.id);
      if (!latest) return;
      try {
        await api.updatePipeline(latest.id, {
          name: latest.name,
          description: latest.description,
          env: latest.env,
          status: latest.status,
          schedule: latest.schedule,
          scheduleConfig: latest.scheduleConfig,
          inputFile: latest.inputFile,
          output: latest.output,
          canvas: latest.canvas,
          steps: (latest.steps || []).map((s, i) => ({
            order: s.order ?? i, type: s.type, x: s.x ?? 0, y: s.y ?? 0,
            label: s.label || s.type, disabled: !!s.disabled, config: s.config || {},
          })),
        });
      } catch (e) {
        setToast({ kind: 'error', title: 'Save failed', detail: e.message });
      }
    }, 200);
    saveTimersRef.current.set(nextPipeline.id, tid);
  }, [projectId]);

  const createPipeline = async (spec) => {
    if (!project) return;
    try {
      const created = await api.createPipeline(project.id, {
        name: spec.name,
        description: spec.description || '',
        mode: spec.mode || 'blank',
        dupSourceId: spec.dupSourceId,
      });
      setProjects(prev => prev.map(pr => pr.id !== project.id ? pr : {
        ...pr,
        pipelines: [...pr.pipelines, created],
      }));
      onOpenPipeline(created);
    } catch (e) {
      setToast({ kind: 'error', title: 'Create failed', detail: e.message });
    }
  };

  const duplicatePipeline = async (pip) => {
    try {
      const copy = await api.duplicatePipeline(pip.id);
      setProjects(prev => prev.map(pr => pr.id !== projectId ? pr : {
        ...pr,
        pipelines: [...pr.pipelines, copy],
      }));
      onOpenPipeline(copy);
    } catch (e) {
      setToast({ kind: 'error', title: 'Duplicate failed', detail: e.message });
    }
  };
  const renamePipeline = async (pip) => {
    const name = window.prompt('Rename pipeline', pip.name);
    if (name == null) return; // cancelled
    const trimmed = name.trim();
    if (!trimmed || trimmed === pip.name) return;
    setProjects(prev => prev.map(pr => pr.id !== projectId ? pr : {
      ...pr,
      pipelines: pr.pipelines.map(pl => pl.id !== pip.id ? pl : { ...pl, name: trimmed }),
    }));
    try {
      await api.updatePipeline(pip.id, { name: trimmed });
    } catch (e) {
      setToast({ kind: 'error', title: 'Rename failed', detail: e.message });
      await reloadCatalog();
    }
  };
  const togglePipelinePause = async (pip) => {
    const nextStatus = pip.status === 'paused' ? 'active' : 'paused';
    setProjects(prev => prev.map(pr => pr.id !== projectId ? pr : {
      ...pr,
      pipelines: pr.pipelines.map(pl => pl.id !== pip.id ? pl : { ...pl, status: nextStatus }),
    }));
    try {
      await api.updatePipeline(pip.id, { status: nextStatus });
    } catch (e) {
      setToast({ kind: 'error', title: 'Save failed', detail: e.message });
      await reloadCatalog();
    }
  };
  const deletePipeline = async (pip) => {
    if (!window.confirm(`Delete pipeline “${pip.name}”? This cannot be undone.`)) return;
    setProjects(prev => prev.map(pr => pr.id !== projectId ? pr : {
      ...pr,
      pipelines: pr.pipelines.filter(pl => pl.id !== pip.id),
    }));
    try {
      await api.deletePipeline(pip.id);
    } catch (e) {
      setToast({ kind: 'error', title: 'Delete failed', detail: e.message });
      await reloadCatalog();
    }
  };

  // Opens the project dialog in edit mode (name + description).
  const renameProject = (p) => setEditingProject(p);
  const saveProjectEdits = async ({ name, description }) => {
    const p = editingProject;
    setEditingProject(null);
    if (!p || (name === p.name && description === (p.description || ''))) return;
    setProjects(prev => prev.map(pr => pr.id !== p.id ? pr : { ...pr, name, description }));
    try {
      await api.updateProject(p.id, { name, description });
    } catch (e) {
      setToast({ kind: 'error', title: 'Saving the project failed', detail: e.message });
      await reloadCatalog();
    }
  };
  const deleteProject = async (p) => {
    const count = p.pipelines?.length || 0;
    const warning = count
      ? `Delete project “${p.name}” and its ${count} pipeline${count === 1 ? '' : 's'}? This cannot be undone.`
      : `Delete project “${p.name}”? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    setProjects(prev => prev.filter(pr => pr.id !== p.id));
    try {
      await api.deleteProject(p.id);
    } catch (e) {
      setToast({ kind: 'error', title: 'Delete failed', detail: e.message });
      await reloadCatalog();
    }
  };

  // Source block → "Refresh schema": re-read the COPY already stored on the
  // server for this pipeline's input and refresh the preview from it. This
  // is NOT a re-read of the file at its original location on disk — once a
  // file is uploaded, the browser/server has no further access to wherever
  // it came from, so an edit made to the file outside the app (even to the
  // very same path that was uploaded) is invisible here until it's
  // re-uploaded via "Replace…"/"Change…" above. This button exists for the
  // case where the server's stored copy itself changed by some other means
  // (e.g. a chained pipeline re-running), not for picking up local edits.
  const onRefreshSchema = async () => {
    if (!pipeline?.inputFile?.storagePath) {
      setToast({ kind: 'warn', title: 'Nothing to refresh', detail: 'This pipeline has no stored input file yet — upload one first.' });
      return;
    }
    try {
      const res = await api.getInputData(pipeline.id);
      const rows = (res.rows || []).map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i }));
      const newCols = res.columns || [];
      const unchanged = uploadedData.rows.length === rows.length
        && uploadedData.columns.length === newCols.length
        && uploadedData.columns.every((c, i) => c.name === newCols[i]?.name);
      setUploadedData({ rows, columns: newCols, fileName: pipeline.inputFile.name || null });
      if (unchanged) {
        setToast({
          kind: 'warn', title: 'Schema unchanged',
          detail: 'This re-reads the copy of the file already stored for this pipeline — not the file on your computer. If you edited the file on disk, use "Replace…"/"Change…" above to upload the new version.',
        });
      } else {
        setToast({ kind: 'ok', title: 'Schema refreshed', detail: `${newCols.length} columns · ${rows.length} rows` });
      }
    } catch (e) {
      setToast({ kind: 'error', title: 'Refresh failed', detail: e.message });
    }
  };

  const onRun = async ({ folder, filename, format, saveAs, appendSheetName }) => {
    if (!pipeline) return;
    // Run on the server, then trigger the download by opening the server's
    // output endpoint. The server already wrote the file and recorded the
    // Run + StepRun rows — we just append to the local history copy.
    // Sources (sheets, uploaded files, step outputs) are resolved server-side
    // from the DB now, so Run sees the same sheets/files the editor does —
    // this used to be a known gap ("Phase 5 cleanup"; see plan.md), fixed as
    // a side effect of the registry living in the DB instead of the client.
    try {
      const newRun = await api.runPipeline(pipeline.id, {
        inputRows: pipeline.inputFile?.storagePath ? [] : uploadedData.rows,
        inputFileName: pipeline.inputFile?.name || uploadedData.fileName || 'input.csv',
        folder, filename, format, saveAs, appendSheetName,
      });
      setLastRunId(newRun.id);
      setRuns(prev => [newRun, ...prev]);
      // Keep the pipelines list's Last run / Rows columns current without
      // reloading the catalog.
      const lastRun = { lastRunStatus: newRun.status, lastRunAt: new Date().toISOString(), rowsProcessed: newRun.rowsOut };
      setProjects(prev => prev.map(pr => {
        if (pr.id !== projectId) return pr;
        const pipelines = pr.pipelines.map(pl => pl.id === pipeline.id ? { ...pl, ...lastRun } : pl);
        return {
          ...pr,
          pipelines,
          lastRunStatus: lastRun.lastRunStatus,
          lastRunAt: lastRun.lastRunAt,
          rowsProcessed: pipelines.reduce((a, pl) => a + (pl.rowsProcessed || 0), 0),
        };
      }));
      if (newRun.status === 'done') {
        // Trigger download — anchor click bypasses popup blockers.
        const a = document.createElement('a');
        a.href = api.runOutputUrl(newRun.id);
        a.download = (newRun.outputPath || '').split('/').pop() || '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setToast({ kind: 'error', title: 'Run failed', detail: newRun.error || 'See run history' });
      }
      // The Run modal waits on this result before it says the run is
      // complete, and opens run details itself.
      return newRun;
    } catch (e) {
      setToast({ kind: 'error', title: 'Run failed', detail: e.message });
      return null;
    }
  };

  // Load run history whenever the editor opens a different pipeline.
  React.useEffect(() => {
    if (!pipelineId) { setRuns([]); return; }
    (async () => {
      try {
        const list = await api.listRuns(pipelineId);
        setRuns(list);
      } catch (_e) {
        setRuns([]);
      }
    })();
  }, [pipelineId]);

  // Hydrate uploadedData from the server when opening a pipeline. Never
  // falls back to the demo sample rows for a real pipeline: a pipeline whose
  // stored file can't be read (e.g. the server restarting) used to show the
  // built-in sample as its source, which looked like the file had changed.
  const hydratedPipelineRef = React.useRef(null);
  const hydratePl = project?.pipelines?.find(p => p.id === pipelineId);
  const hydratePlRef = React.useRef(hydratePl);
  hydratePlRef.current = hydratePl;
  const hydrateReady = !!hydratePl;
  React.useEffect(() => {
    if (!pipelineId) {
      setUploadedData({ rows: SOURCE_ROWS, columns: SOURCE_COLUMNS, fileName: null });
      hydratedPipelineRef.current = null;
      return undefined;
    }
    // Wait for the catalog: deciding before the pipeline is known used to
    // mark it hydrated with the demo rows.
    const pl = hydratePlRef.current;
    if (!pl) return undefined;
    if (hydratedPipelineRef.current === pipelineId) return undefined;
    hydratedPipelineRef.current = pipelineId;
    // A later answer for a pipeline that is no longer open is dropped.
    const stale = () => hydratedPipelineRef.current !== pipelineId;
    if (pl.inputFile?.storagePath) {
      setUploadedData({ rows: [], columns: [], fileName: pl.inputFile.name || null });
      const load = (attempt) => api.getInputData(pipelineId)
        .then((res) => {
          if (stale()) return;
          // Always reflect what the server actually has, even zero rows
          // (e.g. a blank build target, or a genuinely empty file).
          const rows = (res.rows || []).map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i }));
          setUploadedData({ rows, columns: res.columns || [], fileName: pl.inputFile.name || null });
        })
        .catch((e) => {
          if (stale()) return;
          if (attempt < 3) { setTimeout(() => !stale() && load(attempt + 1), 1500 * attempt); return; }
          setToast({ kind: 'error', title: 'Couldn\'t load the source file', detail: `${pl.inputFile.name}: ${e.message}. Reopen the pipeline to try again.` });
          hydratedPipelineRef.current = null;
        });
      load(1);
    } else {
      // No stored upload: a catalog sample shows its own rows, anything
      // else shows no rows rather than a different file's.
      const demo = CATALOG_DATASETS[pl.inputFile?.name];
      setUploadedData(demo
        ? { rows: demo.rows.map((r, i) => ({ ...r, id: r.id || `row_${i}`, __rowIndex: i })), columns: demo.columns, fileName: pl.inputFile.name }
        : { rows: [], columns: [], fileName: null });
    }
    return undefined;
  // Re-run once the catalog has this pipeline.
  }, [pipelineId, hydrateReady]);

  // Apply accent override to root by writing a CSS var (so canvas colors update)
  React.useEffect(() => {
    document.documentElement.style.setProperty('--accent-override', tweaks.accent);
  }, [tweaks.accent]);

  if (!bootstrapped) {
    // While we wait for /me to resolve, render the app shell background so the
    // login screen never flashes for already-authenticated visitors.
    return <div style={{ height: '100vh', background: C.app }} />;
  }

  if (!user) {
    // The normal URL signs in through RAPFlow only; the mock email login is
    // served at its own URL for development and demos.
    if (!isMockLoginPath()) return <SsoSignInScreen error={ssoError} />;
    return (
      <LoginScreen
        onLogin={async (u) => {
          try {
            const me = await api.login({ email: u.email, password: u.password, organizationId: u.organization });
            setUser({ ...me, sso: u.sso });
            await reloadCatalog();
          } catch (e) {
            setToast({ kind: 'error', title: 'Sign-in failed', detail: e.message });
          }
        }}
        organizationName="acme-data-eng"
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.app }}>
      <TopBar
        project={project} pipeline={pipeline} view={view}
        onHome={onHome}
        onBackToPipelines={onBackToPipelines}
        onRun={() => setRunOpen(true)}
        onOpenRuns={() => setRunsOpen(true)}
        onReplaceInput={() => replaceInputRef.current?.()}
        onExportPipeline={exportPipeline}
        onSetEnv={(env) => setEnvRef.current?.(env)}
        onSave={onSave}
        user={user}
        onLogout={async () => {
          try { await api.logout(); } catch { /* ignore — clear local state regardless */ }
          setUser(null); setProjects([]); setRuns([]); setOrgs([]);
          setView('projects'); setProjectId(null); setPipelineId(null);
        }}
        onSwitchOrg={async (orgName) => {
          try {
            const me = await api.switchOrg(orgName);
            setUser(u => u ? { ...me, sso: u.sso } : me);
            await reloadCatalog();
            setView('projects'); setProjectId(null); setPipelineId(null);
            setToast({ kind: 'ok', title: 'Organization switched', detail: orgName, detailMono: true });
          } catch (e) {
            setToast({ kind: 'error', title: 'Switch failed', detail: e.message });
          }
        }}
        orgs={orgs}
        dirty={dirty}
        panels={panels}
        togglePanel={togglePanel}
        theme={theme}
        onToggleTheme={onToggleTheme}
      />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflowY: 'auto', overflowX: 'hidden', flexDirection: 'column' }}>
        {view === 'projects' && (
          <ProjectsScreen
            projects={projects}
            orgName={user?.organization}
            onOpen={onOpenProject}
            onNew={() => setNewProjectOpen(true)}
            onImport={importProject}
            onRename={renameProject}
            onDelete={deleteProject}
          />
        )}
        <NewProjectModal
          open={newProjectOpen}
          onClose={() => setNewProjectOpen(false)}
          onCreate={createProject}
          organizationName="acme-data-eng"
        />
        <NewProjectModal
          open={!!editingProject}
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={saveProjectEdits}
          organizationName="acme-data-eng"
        />
        {view === 'pipelines' && project && (
          <PipelinesScreen
            project={project}
            allProjects={projects}
            onBack={onHome}
            onOpenPipeline={onOpenPipeline}
            onNewPipeline={createPipeline}
            onDuplicatePipeline={duplicatePipeline}
            onRenamePipeline={renamePipeline}
            onTogglePause={togglePipelinePause}
            onDeletePipeline={deletePipeline}
            onImportPipeline={importPipeline}
            onExportProject={() => exportProject(project)}
            onExportPipeline={exportPipeline}
            onSetEnv={setPipelineEnv}
            onSchedulePipeline={(pip) => setSchedulePipeline(pip)}
          />
        )}
        <SchedulePipelineModal
          open={!!schedulePipeline}
          onClose={() => setSchedulePipeline(null)}
          pipeline={schedulePipeline}
          onSave={async (patch) => {
            const target = schedulePipeline;
            setProjects(prev => prev.map(pr => ({
              ...pr,
              pipelines: pr.pipelines.map(pl =>
                pl.id === target.id ? { ...pl, ...patch } : pl),
            })));
            try {
              await api.patchSchedule(target.id, {
                schedule: patch.schedule,
                scheduleConfig: patch.scheduleConfig,
                status: patch.status,
              });
            } catch (e) {
              setToast({ kind: 'error', title: 'Save failed', detail: e.message });
              await reloadCatalog();
            }
            setSchedulePipeline(null);
            setToast({
              kind: 'ok',
              title: 'Schedule updated',
              detail: `${target.name} · ${patch.schedule}`,
            });
          }}
        />
        {view === 'pipeline' && project && pipeline && (
          <PipelineEditor
            project={project}
            projects={projects}
            pipeline={pipeline}
            onUpdatePipeline={updatePipeline}
            density={tweaks.compactNodes ? 'compact' : 'normal'}
            accentOverride={tweaks.accent}
            dirty={dirty}
            setDirty={setDirty}
            onRun={() => setRunOpen(true)}
            onOpenRuns={() => setRunsOpen(true)}
            lastRunId={lastRunId}
            panels={panels}
            togglePanel={togglePanel}
            uploadedData={uploadedData}
            onUploadData={setUploadedData}
            onToast={setToast}
            onRefreshSchema={onRefreshSchema}
          />
        )}
      </div>

      {/* Modals */}
      {view === 'pipeline' && pipeline && (
        <RunPipelineModal
          open={runOpen}
          onClose={() => setRunOpen(false)}
          pipeline={pipeline}
          onRun={onRun}
          onViewDetails={() => setRunsOpen(true)}
          lastRunId={lastRunId}
        />
      )}
      <RunHistoryDrawer
        open={runsOpen}
        onClose={() => setRunsOpen(false)}
        runs={runs}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Tweaks panel */}
      {(
        <TweaksPanel title="Tweaks">
          <TweakSection title="Look">
            <TweakColor
              label="Accent color"
              value={tweaks.accent}
              onChange={(v) => setTweak('accent', v)}
              options={['#3b82f6', '#34d399', '#a78bfa', '#fbbf24', '#f87171']}
            />
            <TweakToggle
              label="Canvas dot grid"
              value={tweaks.canvasDots}
              onChange={(v) => setTweak('canvasDots', v)}
            />
            <TweakToggle
              label="Compact nodes"
              value={tweaks.compactNodes}
              onChange={(v) => setTweak('compactNodes', v)}
            />
          </TweakSection>
          <TweakSection title="Data preview">
            <TweakToggle
              label="Show column types"
              value={tweaks.showColumnTypes}
              onChange={(v) => setTweak('showColumnTypes', v)}
            />
            <TweakToggle
              label="Show row count badges on edges"
              value={tweaks.showStepBadges}
              onChange={(v) => setTweak('showStepBadges', v)}
            />
          </TweakSection>
        </TweaksPanel>
      )}
    </div>
  );
}

// Mounted by src/main.jsx — keep App as the default export.
export default App;

